import React, { useState, useEffect, useRef } from 'react';

const API_BASE_URL = 'https://shop.neoteeforge.com/neotee-backend';

const getTokenFromStorage = () => {
  const possibleKeys = [
    'neoteeforge_auth_token',
    'token',
    'auth_token',
    'neotee_auth'
  ];
  
  for (const key of possibleKeys) {
    const value = localStorage.getItem(key);
    if (value) {
      if (key === 'neotee_auth') {
        try {
          const authData = JSON.parse(value);
          if (authData.token) {
            return authData.token;
          }
        } catch (e) {
          console.error('Error parsing legacy auth data:', e);
        }
      }
      return value;
    }
  }
  
  return null;
};

const AdminDashboard = ({ auth, onBack }) => {
  // Main states
  const [stats, setStats] = useState({
    overview: {
      totalUsers: 0,
      totalProducts: 0,
      totalOrders: 0,
      totalRevenue: 0,
      pendingOrders: 0,
      completedOrders: 0,
      cancelledOrders: 0
    },
    today: {
      orders: 0,
      revenue: 0,
      newUsers: 0
    },
    recentOrders: []
  });
  
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [payouts, setPayouts] = useState([]);
  
  // Loading states
  const [loading, setLoading] = useState({ 
    dashboard: false, 
    products: false, 
    orders: false, 
    users: false,
    categories: false,
    keywords: false,
    sellers: false,
    payouts: false
  });
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dbError, setDbError] = useState(null);
  
  // Product Form States
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    description: '',
    price: '',
    mrp_price: '',
    sale_price: '',
    category_id: '',
    featured: false,
    rating: '4.00',
    tagline: '',
    thumbnail_url: '',
    stock_quantity: 10,
    allow_download: true,
    allow_online_read: true,
    pdf_file: null,
    pdf_file_url: '',
    sample_pdf: null,
    sample_pdf_url: '',
    product_images: [],
    existing_images: [],
    selected_keywords: [],
    available_keywords: [],
    
    // NEW FIELDS
    gst_included: false,
    default_discount: 0,
    coupon_code: '',
    coupon_discount: 0,
    seo_keywords: '',
    meta_description: ''
  });
  
  // Category Management States
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: '',
    status: 1,
    icon: '📚'
  });
  
  // Keyword Management States
  const [showKeywordForm, setShowKeywordForm] = useState(false);
  const [editingKeyword, setEditingKeyword] = useState(null);
  const [keywordFormData, setKeywordFormData] = useState({
    keyword: '',
    description: '',
    type: 'tag',
    status: 1
  });
  
  // Seller Management States
  const [showSellerForm, setShowSellerForm] = useState(false);
  const [editingSeller, setEditingSeller] = useState(null);
  const [sellerFormData, setSellerFormData] = useState({
    user_id: '',
    store_name: '',
    store_description: '',
    commission_rate: 15,
    status: 'pending',
    contact_email: '',
    contact_phone: '',
    store_address: '',
    tax_id: '',
    bank_account_number: '',
    bank_name: '',
    ifsc_code: ''
  });
  
  // Payout Management States
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [payoutFormData, setPayoutFormData] = useState({
    amount: '',
    payment_method: 'bank_transfer',
    transaction_id: '',
    notes: ''
  });
  
  // Product Keywords Management
  const [showProductKeywordsModal, setShowProductKeywordsModal] = useState(false);
  const [selectedProductForKeywords, setSelectedProductForKeywords] = useState(null);
  const [productKeywords, setProductKeywords] = useState([]);
  const [availableKeywordsForProduct, setAvailableKeywordsForProduct] = useState([]);
  
  // Image Management States
  const [productImages, setProductImages] = useState([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [showImageManager, setShowImageManager] = useState(false);
  const [selectedProductForImages, setSelectedProductForImages] = useState(null);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const [loadingImages, setLoadingImages] = useState(false);
  
  // Image reordering states
  const [draggedImage, setDraggedImage] = useState(null);
  const [dragOverImage, setDragOverImage] = useState(null);
  
  // File input refs
  const pdfFileInputRef = useRef(null);
  const samplePdfFileInputRef = useRef(null);
  const imageFileInputRef = useRef(null);
  
  const [uploading, setUploading] = useState(false);
  const [pdfUploadProgress, setPdfUploadProgress] = useState({ main: 0, sample: 0 });
  
  const [notification, setNotification] = useState({ 
    show: false, 
    message: '', 
    type: '' 
  });

  // Calculate price display
  const calculatePriceDisplay = () => {
    const mrp = parseFloat(formData.mrp_price) || parseFloat(formData.price) || 0;
    const salePrice = parseFloat(formData.sale_price) || 0;
    const defaultDiscount = parseFloat(formData.default_discount) || 0;
    const couponDiscount = parseFloat(formData.coupon_discount) || 0;
    const gstIncluded = formData.gst_included;
    
    const basePrice = salePrice > 0 ? salePrice : mrp;
    const effectiveDiscount = Math.max(defaultDiscount, couponDiscount);
    const discountedPrice = basePrice - (basePrice * effectiveDiscount / 100);
    
    let finalPrice = discountedPrice;
    let gstAmount = 0;
    if (!gstIncluded) {
      gstAmount = discountedPrice * 0.18;
      finalPrice = discountedPrice + gstAmount;
    }
    
    return {
      mrp: mrp,
      salePrice: salePrice,
      basePrice: basePrice,
      effectiveDiscount: effectiveDiscount,
      discountedPrice: discountedPrice,
      gstIncluded: gstIncluded,
      gstAmount: gstAmount,
      finalPrice: finalPrice,
      savings: mrp - finalPrice,
      savingsPercentage: mrp > 0 ? ((mrp - finalPrice) / mrp * 100).toFixed(2) : 0
    };
  };

  // Helper function for API calls
  const fetchAPI = async (endpoint, method = 'GET', body = null, isFormData = false) => {
    const token = getTokenFromStorage();
    
    const headers = {};
    
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const config = {
      method,
      headers,
      credentials: 'include',
      mode: 'cors'
    };

    if (body) {
      config.body = isFormData ? body : JSON.stringify(body);
    }

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
      
      const responseText = await response.text();
      
      let data;
      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch (parseError) {
        data = { message: responseText };
      }
      
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`;
        if (data.message) errorMessage += `: ${data.message}`;
        else if (data.error) errorMessage += `: ${data.error}`;
        else if (response.statusText) errorMessage += `: ${response.statusText}`;
        
        if (response.status === 401) {
          clearAllTokens();
          errorMessage = 'Session expired. Please login again.';
        }
        
        if (data.message && data.message.includes('Unknown column')) {
          setDbError(data.message);
        }
        
        throw new Error(errorMessage);
      }
      
      return data;
      
    } catch (error) {
      console.error('Fetch Error:', error);
      throw error;
    }
  };

  // Upload image function
  const uploadImage = async (productId, imageFile) => {
    try {
      setImageUploading(true);
      setImageUploadProgress(0);
      
      const formData = new FormData();
      formData.append('image', imageFile);
      
      const token = getTokenFromStorage();
      
      const response = await fetch(`${API_BASE_URL}/api/admin/products/${productId}/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
        credentials: 'include',
        mode: 'cors'
      });
      
      const responseText = await response.text();
      let data;
      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch (parseError) {
        data = { message: responseText };
      }
      
      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to upload image');
      }
      
      if (data.success) {
        showNotification('Image uploaded successfully!', 'success');
        // Refresh product images
        await loadProductImages(productId);
        return data.image;
      } else {
        throw new Error(data.message || 'Failed to upload image');
      }
      
    } catch (error) {
      console.error('Upload image error:', error);
      showNotification('Error uploading image: ' + error.message, 'error');
      throw error;
    } finally {
      setImageUploading(false);
      setImageUploadProgress(0);
    }
  };

  // Load product images
  const loadProductImages = async (productId) => {
    if (!productId) return [];
    
    setLoadingImages(true);
    try {
      const data = await fetchAPI(`/api/products/${productId}/images`);
      if (data && data.images) {
        // Sort images by display_order if available
        const sortedImages = data.images.sort((a, b) => {
          if (a.display_order !== undefined && b.display_order !== undefined) {
            return a.display_order - b.display_order;
          }
          // If no display_order, sort by id or is_primary
          if (a.is_primary) return -1;
          if (b.is_primary) return 1;
          return (a.id || 0) - (b.id || 0);
        });
        return sortedImages;
      }
      return [];
    } catch (error) {
      console.error('Error loading product images:', error);
      return [];
    } finally {
      setLoadingImages(false);
    }
  };

  // Delete product image
  const deleteProductImage = async (productId, imageId) => {
    try {
      const result = await fetchAPI(`/api/admin/products/${productId}/images/${imageId}`, 'DELETE');
      if (result.success) {
        showNotification('Image deleted successfully!', 'success');
        // Refresh product images
        const updatedImages = await loadProductImages(productId);
        setFormData(prev => ({
          ...prev,
          existing_images: updatedImages
        }));
        return true;
      } else {
        showNotification('Failed to delete image: ' + (result.message || 'Unknown error'), 'error');
        return false;
      }
    } catch (error) {
      console.error('Delete image error:', error);
      showNotification('Error deleting image: ' + error.message, 'error');
      return false;
    }
  };

  // Set primary image
  const setPrimaryImage = async (productId, imageId) => {
    try {
      const result = await fetchAPI(`/api/admin/products/${productId}/images/${imageId}/primary`, 'PUT');
      if (result.success) {
        showNotification('Primary image updated successfully!', 'success');
        // Refresh product images
        const updatedImages = await loadProductImages(productId);
        setFormData(prev => ({
          ...prev,
          existing_images: updatedImages
        }));
        return true;
      } else {
        showNotification('Failed to set primary image: ' + (result.message || 'Unknown error'), 'error');
        return false;
      }
    } catch (error) {
      console.error('Set primary image error:', error);
      showNotification('Error setting primary image: ' + error.message, 'error');
      return false;
    }
  };

  // Update image order
  const updateImageOrder = async (productId, imageIds) => {
    try {
      const result = await fetchAPI(`/api/admin/products/${productId}/images/reorder`, 'PUT', { image_ids: imageIds });
      if (result.success) {
        showNotification('Image order updated successfully!', 'success');
        return true;
      } else {
        showNotification('Failed to update image order: ' + (result.message || 'Unknown error'), 'error');
        return false;
      }
    } catch (error) {
      console.error('Update image order error:', error);
      showNotification('Error updating image order: ' + error.message, 'error');
      return false;
    }
  };

  // Handle drag start
  const handleDragStart = (e, index) => {
    setDraggedImage(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
    e.target.classList.add('dragging');
  };

  // Handle drag over
  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverImage(index);
  };

  // Handle drag leave
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverImage(null);
  };

  // Handle drop
  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    e.target.classList.remove('dragging');
    
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    
    if (isNaN(dragIndex) || dragIndex === dropIndex) {
      setDraggedImage(null);
      setDragOverImage(null);
      return;
    }

    // Create new array with reordered images
    const newImages = [...formData.existing_images];
    const draggedItem = newImages[dragIndex];
    
    // Remove the dragged item
    newImages.splice(dragIndex, 1);
    // Insert it at the new position
    newImages.splice(dropIndex, 0, draggedItem);
    
    // Update display order
    const updatedImages = newImages.map((img, idx) => ({
      ...img,
      display_order: idx
    }));
    
    setFormData(prev => ({
      ...prev,
      existing_images: updatedImages
    }));
    
    // If editing a product, save the new order to server
    if (editingProduct) {
      const imageIds = updatedImages.map(img => img.id);
      await updateImageOrder(editingProduct.id, imageIds);
    }
    
    setDraggedImage(null);
    setDragOverImage(null);
  };

  // Handle drag end
  const handleDragEnd = (e) => {
    e.target.classList.remove('dragging');
    setDraggedImage(null);
    setDragOverImage(null);
  };

  // Get thumbnail image URL
  const getThumbnailUrl = (product) => {
    try {
      if (product.images && Array.isArray(product.images) && product.images.length > 0) {
        const primaryImage = product.images.find(img => img.is_primary) || product.images[0];
        
        if (primaryImage && primaryImage.thumbnail_name) {
          return `${API_BASE_URL}/uploads/images/${primaryImage.thumbnail_name}`;
        }
        
        if (primaryImage && primaryImage.image_name) {
          return `${API_BASE_URL}/uploads/images/${primaryImage.image_name}`;
        }
      }
      
      if (product.thumbnail_url) {
        if (product.thumbnail_url.startsWith('http')) {
          return product.thumbnail_url;
        }
        return `${API_BASE_URL}${product.thumbnail_url}`;
      }
      
      if (product.primary_image) {
        return `${API_BASE_URL}/uploads/images/${product.primary_image}`;
      }
      
      return 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=400&h=300&fit=crop&auto=format';
      
    } catch (error) {
      console.error('Error getting thumbnail URL:', error);
      return 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=400&h=300&fit=crop&auto=format';
    }
  };

  // Database Fix Functions
  const fixDatabaseTables = async () => {
    try {
      showNotification('Fixing database tables...', 'info');
      
      const setupResult = await fetchAPI('/api/setup-tables');
      
      if (setupResult.success) {
        showNotification('Database tables fixed successfully!', 'success');
        setDbError(null);
        
        if (activeTab === 'categories') {
          await loadCategories();
        } else if (activeTab === 'keywords') {
          await loadKeywords();
        } else if (activeTab === 'sellers') {
          await loadSellers();
        }
      } else {
        showNotification('Failed to fix database tables', 'error');
      }
    } catch (error) {
      console.error('Database fix error:', error);
      showNotification('Error fixing database: ' + error.message, 'error');
    }
  };

  const checkDatabaseConnection = async () => {
    try {
      const result = await fetchAPI('/api/test-db');
      if (result.success) {
        showNotification('Database connection successful', 'success');
      } else {
        showNotification('Database connection failed', 'error');
      }
    } catch (error) {
      console.error('Database check error:', error);
      showNotification('Database check failed: ' + error.message, 'error');
    }
  };

  // Load Dashboard Data
  const loadDashboardData = async () => {
    try {
      setLoading(prev => ({ ...prev, dashboard: true }));
      
      try {
        const data = await fetchAPI('/api/admin/dashboard');
        
        if (data && data.success !== false) {
          const transformedData = transformDashboardData(data.data || data);
          setStats(transformedData);
        } else {
          const calculatedStats = await calculateStatsFromIndividualAPIs();
          setStats(calculatedStats);
        }
      } catch (apiError) {
        console.error('Dashboard API Error:', apiError);
        const calculatedStats = await calculateStatsFromIndividualAPIs();
        setStats(calculatedStats);
      }
      
    } catch (error) {
      console.error('Error loading dashboard:', error);
      const calculatedStats = calculateStatsFromData([], [], []);
      setStats(calculatedStats);
    } finally {
      setLoading(prev => ({ ...prev, dashboard: false }));
    }
  };

  const calculateStatsFromIndividualAPIs = async () => {
    try {
      const [usersData, productsData, ordersData] = await Promise.allSettled([
        fetchAPI('/api/admin/users'),
        fetchAPI('/api/admin/products'),
        fetchAPI('/api/admin/orders')
      ]);
      
      return calculateStatsFromData(
        usersData.status === 'fulfilled' ? usersData.value : [],
        productsData.status === 'fulfilled' ? productsData.value : [],
        ordersData.status === 'fulfilled' ? ordersData.value : []
      );
    } catch (error) {
      console.error('Error calculating stats:', error);
      return getMockStats();
    }
  };

  const calculateStatsFromData = (usersData, productsData, ordersData) => {
    const users = Array.isArray(usersData) ? usersData : (usersData.users || usersData.data || []);
    const products = Array.isArray(productsData) ? productsData : (productsData.products || productsData.data || []);
    const orders = Array.isArray(ordersData) ? ordersData : (ordersData.orders || ordersData.data || []);
    
    const totalRevenue = orders.reduce((sum, order) => {
      return sum + parseFloat(order.total || order.total_amount || 0);
    }, 0);
    
    const today = new Date().toISOString().split('T')[0];
    const todayOrders = orders.filter(order => {
      const orderDate = new Date(order.created_at).toISOString().split('T')[0];
      return orderDate === today;
    });
    
    const todayRevenue = todayOrders.reduce((sum, order) => {
      return sum + parseFloat(order.total || order.total_amount || 0);
    }, 0);
    
    const pendingOrders = orders.filter(order => order.status === 'pending').length;
    const completedOrders = orders.filter(order => order.status === 'delivered').length;
    const cancelledOrders = orders.filter(order => order.status === 'cancelled').length;
    
    const recentOrders = orders
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5)
      .map(order => ({
        id: order.id,
        customer_name: getCustomerName(order),
        total: order.total || order.total_amount || 0,
        status: order.status || 'pending',
        created_at: order.created_at
      }));
    
    return {
      overview: {
        totalUsers: users.length,
        totalProducts: products.length,
        totalOrders: orders.length,
        totalRevenue: totalRevenue,
        pendingOrders: pendingOrders,
        completedOrders: completedOrders,
        cancelledOrders: cancelledOrders
      },
      today: {
        orders: todayOrders.length,
        revenue: todayRevenue,
        newUsers: 0
      },
      recentOrders: recentOrders
    };
  };

  const getCustomerName = (order) => {
    if (order.customer_name) return order.customer_name;
    if (order.shipping_info) {
      try {
        const shippingInfo = typeof order.shipping_info === 'string' 
          ? JSON.parse(order.shipping_info) 
          : order.shipping_info;
        return shippingInfo.fullName || `Order #${order.id}`;
      } catch (e) {
        return `Order #${order.id}`;
      }
    }
    return `User #${order.user_id}`;
  };

  const getMockStats = () => {
    return {
      overview: {
        totalUsers: 0,
        totalProducts: 0,
        totalOrders: 0,
        totalRevenue: 0,
        pendingOrders: 0,
        completedOrders: 0,
        cancelledOrders: 0
      },
      today: {
        orders: 0,
        revenue: 0,
        newUsers: 0
      },
      recentOrders: []
    };
  };

  const transformDashboardData = (apiData) => {
    const defaultData = getMockStats();
    
    if (!apiData) return defaultData;
    
    return {
      overview: {
        totalUsers: apiData.total_users || apiData.totalUsers || defaultData.overview.totalUsers,
        totalProducts: apiData.total_products || apiData.totalProducts || defaultData.overview.totalProducts,
        totalOrders: apiData.total_orders || apiData.totalOrders || defaultData.overview.totalOrders,
        totalRevenue: apiData.total_revenue || apiData.totalRevenue || defaultData.overview.totalRevenue,
        pendingOrders: apiData.pending_orders || apiData.pendingOrders || defaultData.overview.pendingOrders,
        completedOrders: apiData.completed_orders || apiData.completedOrders || defaultData.overview.completedOrders,
        cancelledOrders: apiData.cancelled_orders || apiData.cancelledOrders || defaultData.overview.cancelledOrders
      },
      today: {
        orders: apiData.today_orders || apiData.today?.orders || defaultData.today.orders,
        revenue: apiData.today_revenue || apiData.today?.revenue || defaultData.today.revenue,
        newUsers: apiData.today_users || apiData.today?.newUsers || defaultData.today.newUsers
      },
      recentOrders: apiData.recent_orders || apiData.recentOrders || defaultData.recentOrders
    };
  };

  // Load Sellers
  const loadSellers = async () => {
    try {
      setLoading(prev => ({ ...prev, sellers: true }));
      const data = await fetchAPI('/api/admin/sellers');
      
      if (data && data.success !== false) {
        const sellersArray = Array.isArray(data) ? data : (data.sellers || data.data || []);
        setSellers(sellersArray);
      } else {
        setSellers([]);
      }
    } catch (error) {
      console.error('Error loading sellers:', error);
      showNotification('Failed to load sellers: ' + error.message, 'error');
      setSellers([]);
    } finally {
      setLoading(prev => ({ ...prev, sellers: false }));
    }
  };

  // Load Payouts
  const loadPayouts = async () => {
    try {
      setLoading(prev => ({ ...prev, payouts: true }));
      const data = await fetchAPI('/api/admin/sellers/payouts');
      
      if (data && data.success !== false) {
        const payoutsArray = Array.isArray(data) ? data : (data.payouts || data.data || []);
        setPayouts(payoutsArray);
      } else {
        setPayouts([]);
      }
    } catch (error) {
      console.error('Error loading payouts:', error);
      showNotification('Failed to load payouts: ' + error.message, 'error');
      setPayouts([]);
    } finally {
      setLoading(prev => ({ ...prev, payouts: false }));
    }
  };

  // Handle Seller Status Update
  const handleSellerStatusUpdate = async (sellerId, newStatus) => {
    try {
      const result = await fetchAPI(`/api/admin/sellers/${sellerId}/status`, 'PUT', { status: newStatus });
      if (result.success) {
        await loadSellers();
        showNotification('Seller status updated successfully!', 'success');
      } else {
        showNotification('Failed to update seller status: ' + (result.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Update seller status error:', error);
      showNotification('Error updating seller status: ' + error.message, 'error');
    }
  };

  // Handle Seller Commission Update
  const handleSellerCommissionUpdate = async (sellerId, commissionRate) => {
    try {
      const result = await fetchAPI(`/api/admin/sellers/${sellerId}/commission`, 'PUT', { commission_rate: commissionRate });
      if (result.success) {
        await loadSellers();
        showNotification('Seller commission updated successfully!', 'success');
      } else {
        showNotification('Failed to update commission: ' + (result.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Update commission error:', error);
      showNotification('Error updating commission: ' + error.message, 'error');
    }
  };

  // Handle Process Payout
  const handleProcessPayout = async (payoutId) => {
    try {
      const result = await fetchAPI(`/api/admin/sellers/payouts/${payoutId}/process`, 'POST', payoutFormData);
      if (result.success) {
        setShowPayoutModal(false);
        setSelectedPayout(null);
        resetPayoutForm();
        await loadPayouts();
        await loadSellers();
        showNotification('Payout processed successfully!', 'success');
      } else {
        showNotification('Failed to process payout: ' + (result.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Process payout error:', error);
      showNotification('Error processing payout: ' + error.message, 'error');
    }
  };

  // Open Payout Modal
  const handleOpenPayoutModal = (payout) => {
    setSelectedPayout(payout);
    const seller = sellers.find(s => s.id === payout.seller_id);
    setPayoutFormData({
      amount: payout.amount || '',
      payment_method: 'bank_transfer',
      transaction_id: '',
      notes: `Payout for ${seller?.store_name || 'Seller'} - ${formatDate(payout.requested_at)}`
    });
    setShowPayoutModal(true);
  };

  // Seller Form Handlers
  const handleCreateSeller = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      const result = await fetchAPI('/api/admin/sellers', 'POST', sellerFormData);
      if (result.success) {
        setShowSellerForm(false);
        resetSellerForm();
        await loadSellers();
        showNotification('Seller created successfully!', 'success');
      } else {
        showNotification('Failed to create seller: ' + (result.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Create seller error:', error);
      showNotification('Error creating seller: ' + error.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateSeller = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      const result = await fetchAPI(`/api/admin/sellers/${editingSeller.id}`, 'PUT', sellerFormData);
      if (result.success) {
        setShowSellerForm(false);
        setEditingSeller(null);
        resetSellerForm();
        await loadSellers();
        showNotification('Seller updated successfully!', 'success');
      } else {
        showNotification('Failed to update seller: ' + (result.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Update seller error:', error);
      showNotification('Error updating seller: ' + error.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteSeller = async (sellerId) => {
    if (window.confirm('Are you sure you want to delete this seller? This will also remove all their products.')) {
      try {
        const result = await fetchAPI(`/api/admin/sellers/${sellerId}`, 'DELETE');
        if (result.success) {
          await loadSellers();
          showNotification('Seller deleted successfully!', 'success');
        } else {
          showNotification('Failed to delete seller: ' + (result.message || 'Unknown error'), 'error');
        }
      } catch (error) {
        console.error('Delete seller error:', error);
        showNotification('Error deleting seller: ' + error.message, 'error');
      }
    }
  };

  const handleEditSeller = (seller) => {
    setEditingSeller(seller);
    setSellerFormData({
      user_id: seller.user_id || '',
      store_name: seller.store_name || '',
      store_description: seller.store_description || '',
      commission_rate: seller.commission_rate || 15,
      status: seller.status || 'pending',
      contact_email: seller.contact_email || '',
      contact_phone: seller.contact_phone || '',
      store_address: seller.store_address || '',
      tax_id: seller.tax_id || '',
      bank_account_number: seller.bank_account_number || '',
      bank_name: seller.bank_name || '',
      ifsc_code: seller.ifsc_code || ''
    });
    setShowSellerForm(true);
  };

  // Reset Forms
  const resetSellerForm = () => {
    setSellerFormData({
      user_id: '',
      store_name: '',
      store_description: '',
      commission_rate: 15,
      status: 'pending',
      contact_email: '',
      contact_phone: '',
      store_address: '',
      tax_id: '',
      bank_account_number: '',
      bank_name: '',
      ifsc_code: ''
    });
  };

  const resetPayoutForm = () => {
    setPayoutFormData({
      amount: '',
      payment_method: 'bank_transfer',
      transaction_id: '',
      notes: ''
    });
  };

  // Load Products
  const loadProducts = async () => {
    try {
      setLoading(prev => ({ ...prev, products: true }));
      
      try {
        const data = await fetchAPI('/api/admin/products');
        
        if (data && (data.success || Array.isArray(data) || data.products)) {
          const productsArray = Array.isArray(data) ? data : (data.products || data.data || []);
          const filteredProducts = productsArray.filter(product => 
            product.title && product.title.trim() !== ''
          );
          
          console.log('Loaded products:', filteredProducts.length);
          setProducts(filteredProducts);
        } else {
          setProducts([]);
        }
      } catch (apiError) {
        console.error('Products API Error:', apiError);
        setProducts([]);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    } finally {
      setLoading(prev => ({ ...prev, products: false }));
    }
  };

  // Load Orders
  const loadOrders = async () => {
    try {
      setLoading(prev => ({ ...prev, orders: true }));
      
      try {
        const ordersData = await fetchAPI('/api/admin/orders');
        const ordersArray = Array.isArray(ordersData) ? ordersData : (ordersData.orders || ordersData.data || []);
        setOrders(ordersArray);
      } catch (apiError) {
        console.error('Orders API Error:', apiError);
        setOrders([]);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
      setOrders([]);
    } finally {
      setLoading(prev => ({ ...prev, orders: false }));
    }
  };

  // Load Users
  const loadUsers = async () => {
    try {
      setLoading(prev => ({ ...prev, users: true }));
      
      try {
        const usersData = await fetchAPI('/api/admin/users');
        const usersArray = Array.isArray(usersData) ? usersData : (usersData.users || usersData.data || []);
        setUsers(usersArray);
      } catch (apiError) {
        console.error('Users API Error:', apiError);
        setUsers([]);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    } finally {
      setLoading(prev => ({ ...prev, users: false }));
    }
  };

  // Load Categories
  const loadCategories = async () => {
    try {
      setLoading(prev => ({ ...prev, categories: true }));
      setDbError(null);
      
      const data = await fetchAPI('/api/categories');
      
      if (data && data.success !== false) {
        const categoriesArray = Array.isArray(data) ? data : (data.categories || data || []);
        const transformedCategories = categoriesArray.map(cat => ({
          id: cat.id,
          name: cat.name || '',
          description: cat.description || '',
          status: cat.status || 1,
          icon: cat.icon || '📚',
          display_order: cat.display_order || 0
        }));
        
        setCategories(transformedCategories);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      showNotification('Failed to load categories: ' + error.message, 'error');
      setCategories([]);
    } finally {
      setLoading(prev => ({ ...prev, categories: false }));
    }
  };

  // Load Keywords
  const loadKeywords = async () => {
    try {
      setLoading(prev => ({ ...prev, keywords: true }));
      setDbError(null);
      
      const data = await fetchAPI('/api/keywords');
      
      if (data && data.success !== false) {
        const keywordsArray = Array.isArray(data) ? data : (data.keywords || data || []);
        setKeywords(keywordsArray);
      } else {
        setKeywords([]);
      }
    } catch (error) {
      console.error('Error loading keywords:', error);
      showNotification('Failed to load keywords: ' + error.message, 'error');
      setKeywords([]);
    } finally {
      setLoading(prev => ({ ...prev, keywords: false }));
    }
  };

  // Handle Create Product
  const handleCreateProduct = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    try {
      const priceData = calculatePriceDisplay();
      const finalPrice = priceData.finalPrice || parseFloat(formData.price) || 0;
      
      const productData = {
        title: formData.title,
        author: formData.author || '',
        description: formData.description || '',
        price: finalPrice,
        mrp_price: parseFloat(formData.mrp_price) || parseFloat(formData.price) || 0,
        sale_price: parseFloat(formData.sale_price) || 0,
        category_id: parseInt(formData.category_id),
        tagline: formData.tagline || '',
        rating: parseFloat(formData.rating),
        stock_quantity: parseInt(formData.stock_quantity),
        featured: formData.featured ? 1 : 0,
        thumbnail_url: '',
        allow_download: formData.allow_download ? 1 : 0,
        allow_online_read: formData.allow_online_read ? 1 : 0,
        
        gst_included: formData.gst_included ? 1 : 0,
        default_discount: parseFloat(formData.default_discount) || 0,
        coupon_code: formData.coupon_code || '',
        coupon_discount: parseFloat(formData.coupon_discount) || 0,
        seo_keywords: formData.seo_keywords || '',
        meta_description: formData.meta_description || ''
      };

      const result = await fetchAPI('/api/admin/products', 'POST', productData);
      
      if (result.success && result.product_id) {
        const productId = result.product_id;
        
        // Upload PDF files if provided
        if (formData.pdf_file) {
          await uploadPdfFile(productId, formData.pdf_file, false);
        }
        
        if (formData.sample_pdf) {
          await uploadPdfFile(productId, formData.sample_pdf, true);
        }
        
        // Upload images if provided
        if (formData.product_images && formData.product_images.length > 0) {
          for (const imageFile of formData.product_images) {
            await uploadImage(productId, imageFile);
          }
        }
        
        setShowProductForm(false);
        resetForm();
        loadProducts();
        showNotification('Product created successfully!', 'success');
      } else {
        showNotification('Failed to create product: ' + (result.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Create product error:', error);
      showNotification('Error creating product: ' + error.message, 'error');
    } finally {
      setUploading(false);
      setPdfUploadProgress({ main: 0, sample: 0 });
    }
  };

  // Upload PDF file
  const uploadPdfFile = async (productId, pdfFile, isSample = false) => {
    try {
      const formData = new FormData();
      formData.append('pdf', pdfFile);
      
      const endpoint = isSample 
        ? `/api/admin/books/${productId}/upload-sample`
        : `/api/admin/books/${productId}/upload-pdf`;
      
      const result = await fetchAPI(endpoint, 'POST', formData, true);
      
      if (result.success) {
        showNotification(`${isSample ? 'Sample' : 'Main'} PDF uploaded successfully!`, 'success');
        return true;
      } else {
        showNotification(`Failed to upload ${isSample ? 'sample' : 'main'} PDF: ${result.message || 'Unknown error'}`, 'error');
        return false;
      }
    } catch (error) {
      console.error('Upload PDF error:', error);
      showNotification(`Error uploading ${isSample ? 'sample' : 'main'} PDF: ${error.message}`, 'error');
      return false;
    }
  };

  // Handle Update Product
  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    try {
      const priceData = calculatePriceDisplay();
      const finalPrice = priceData.finalPrice || parseFloat(formData.price) || 0;
      
      const productData = {
        title: formData.title,
        author: formData.author || '',
        description: formData.description || '',
        price: finalPrice,
        mrp_price: parseFloat(formData.mrp_price) || parseFloat(formData.price) || 0,
        sale_price: parseFloat(formData.sale_price) || 0,
        category_id: parseInt(formData.category_id),
        tagline: formData.tagline || '',
        rating: parseFloat(formData.rating),
        stock_quantity: parseInt(formData.stock_quantity),
        featured: formData.featured ? 1 : 0,
        thumbnail_url: formData.thumbnail_url,
        allow_download: formData.allow_download ? 1 : 0,
        allow_online_read: formData.allow_online_read ? 1 : 0,
        
        gst_included: formData.gst_included ? 1 : 0,
        default_discount: parseFloat(formData.default_discount) || 0,
        coupon_code: formData.coupon_code || '',
        coupon_discount: parseFloat(formData.coupon_discount) || 0,
        seo_keywords: formData.seo_keywords || '',
        meta_description: formData.meta_description || ''
      };

      const result = await fetchAPI(`/api/admin/products/${editingProduct.id}`, 'PUT', productData);
      
      if (result.success) {
        // Upload new PDF files if provided
        if (formData.pdf_file) {
          await uploadPdfFile(editingProduct.id, formData.pdf_file, false);
        }
        
        if (formData.sample_pdf) {
          await uploadPdfFile(editingProduct.id, formData.sample_pdf, true);
        }
        
        // Upload new images if provided
        if (formData.product_images && formData.product_images.length > 0) {
          for (const imageFile of formData.product_images) {
            await uploadImage(editingProduct.id, imageFile);
          }
        }
        
        setShowProductForm(false);
        setEditingProduct(null);
        resetForm();
        loadProducts();
        showNotification('Product updated successfully!', 'success');
      } else {
        showNotification('Failed to update product: ' + (result.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Update product error:', error);
      showNotification('Error updating product: ' + error.message, 'error');
    } finally {
      setUploading(false);
      setPdfUploadProgress({ main: 0, sample: 0 });
    }
  };

  // Handle Edit Product - IMPROVED VERSION WITH IMAGE LOADING
  const handleEditProduct = async (product) => {
    setEditingProduct(product);
    setShowProductForm(true);
    setLoadingImages(true);
    
    try {
      // Load product images for editing
      const productImages = await loadProductImages(product.id);
      
      // Sort images by display_order if available
      const sortedImages = productImages.sort((a, b) => {
        if (a.display_order !== undefined && b.display_order !== undefined) {
          return a.display_order - b.display_order;
        }
        // If no display_order, primary image first, then by id
        if (a.is_primary) return -1;
        if (b.is_primary) return 1;
        return (a.id || 0) - (b.id || 0);
      });
      
      setFormData({
        title: product.title,
        author: product.author || '',
        description: product.description || '',
        price: product.price.toString(),
        mrp_price: product.mrp_price?.toString() || product.price.toString(),
        sale_price: product.sale_price?.toString() || '',
        category_id: product.category_id?.toString() || '',
        tagline: product.tagline || '',
        featured: product.featured === 1,
        rating: product.rating?.toString() || '4.00',
        thumbnail_url: product.thumbnail_url || '',
        stock_quantity: product.stock_quantity || 10,
        allow_download: product.allow_download !== undefined ? product.allow_download : true,
        allow_online_read: product.allow_online_read !== undefined ? product.allow_online_read : true,
        pdf_file: null,
        pdf_file_url: product.pdf_file 
          ? `${API_BASE_URL}/uploads/books/${product.pdf_file}`
          : '',
        sample_pdf: null,
        sample_pdf_url: product.sample_pdf 
          ? `${API_BASE_URL}/uploads/books/${product.sample_pdf}`
          : '',
        product_images: [],
        existing_images: sortedImages || [],
        selected_keywords: product.keywords || [],
        available_keywords: [],
        
        gst_included: product.gst_included === 1,
        default_discount: product.default_discount || 0,
        coupon_code: product.coupon_code || '',
        coupon_discount: product.coupon_discount || 0,
        seo_keywords: product.seo_keywords || '',
        meta_description: product.meta_description || ''
      });
      
      if (sortedImages.length > 0) {
        showNotification(`Loaded ${sortedImages.length} images for this product`, 'info');
      }
    } catch (error) {
      console.error('Error loading product images:', error);
      showNotification('Error loading product images', 'error');
    } finally {
      setLoadingImages(false);
    }
  };

  // Handle Delete Product
  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        const result = await fetchAPI(`/api/admin/products/${productId}`, 'DELETE');
        if (result.success) {
          loadProducts();
          showNotification('Product deleted successfully!', 'success');
        } else {
          showNotification('Failed to delete product: ' + (result.message || 'Unknown error'), 'error');
        }
      } catch (error) {
        console.error('Delete product error:', error);
        showNotification('Error deleting product: ' + error.message, 'error');
      }
    }
  };

  // Image Upload Handlers
  const handleImageFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    
    const validFiles = files.filter(file => validImageTypes.includes(file.type));
    
    if (validFiles.length !== files.length) {
      showNotification('Some files were not images (JPEG, PNG, GIF, WebP only)', 'warning');
    }
    
    if (validFiles.length > 0) {
      setFormData(prev => ({
        ...prev,
        product_images: [...prev.product_images, ...validFiles]
      }));
      showNotification(`${validFiles.length} image(s) added`, 'success');
    }
    
    // Reset file input
    e.target.value = '';
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      product_images: prev.product_images.filter((_, i) => i !== index)
    }));
  };

  const handleRemoveExistingImage = async (imageId) => {
    if (editingProduct) {
      const confirmed = window.confirm('Are you sure you want to delete this image?');
      if (confirmed) {
        await deleteProductImage(editingProduct.id, imageId);
      }
    }
  };

  const handleSetPrimaryImage = async (imageId) => {
    if (editingProduct) {
      await setPrimaryImage(editingProduct.id, imageId);
    }
  };

  // Category Management
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      const result = await fetchAPI('/api/categories', 'POST', categoryFormData);
      if (result && result.success) {
        setShowCategoryForm(false);
        resetCategoryForm();
        loadCategories();
        showNotification('Category created successfully!', 'success');
      } else {
        showNotification('Failed to create category: ' + (result?.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Create category error:', error);
      showNotification('Error creating category: ' + error.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      const result = await fetchAPI(`/api/categories/${editingCategory.id}`, 'PUT', categoryFormData);
      if (result && result.success) {
        setShowCategoryForm(false);
        setEditingCategory(null);
        resetCategoryForm();
        loadCategories();
        showNotification('Category updated successfully!', 'success');
      } else {
        showNotification('Failed to update category: ' + (result?.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Update category error:', error);
      showNotification('Error updating category: ' + error.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        const result = await fetchAPI(`/api/categories/${categoryId}`, 'DELETE');
        if (result && result.success) {
          loadCategories();
          showNotification('Category deleted successfully!', 'success');
        } else {
          showNotification('Failed to delete category: ' + (result?.message || 'Unknown error'), 'error');
        }
      } catch (error) {
        console.error('Delete category error:', error);
        showNotification('Error deleting category: ' + error.message, 'error');
      }
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name || '',
      description: category.description || '',
      status: category.status || 1,
      icon: category.icon || '📚'
    });
    setShowCategoryForm(true);
  };

  // Keyword Management
  const handleCreateKeyword = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      const result = await fetchAPI('/api/keywords', 'POST', keywordFormData);
      if (result && result.success) {
        setShowKeywordForm(false);
        resetKeywordForm();
        loadKeywords();
        showNotification('Keyword created successfully!', 'success');
      } else {
        showNotification('Failed to create keyword: ' + (result?.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Create keyword error:', error);
      showNotification('Error creating keyword: ' + error.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateKeyword = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      const result = await fetchAPI(`/api/keywords/${editingKeyword.id}`, 'PUT', keywordFormData);
      if (result && result.success) {
        setShowKeywordForm(false);
        setEditingKeyword(null);
        resetKeywordForm();
        loadKeywords();
        showNotification('Keyword updated successfully!', 'success');
      } else {
        showNotification('Failed to update keyword: ' + (result?.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Update keyword error:', error);
      showNotification('Error updating keyword: ' + error.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteKeyword = async (keywordId) => {
    if (window.confirm('Are you sure you want to delete this keyword?')) {
      try {
        const result = await fetchAPI(`/api/keywords/${keywordId}`, 'DELETE');
        if (result && result.success) {
          loadKeywords();
          showNotification('Keyword deleted successfully!', 'success');
        } else {
          showNotification('Failed to delete keyword: ' + (result?.message || 'Unknown error'), 'error');
        }
      } catch (error) {
        console.error('Delete keyword error:', error);
        showNotification('Error deleting keyword: ' + error.message, 'error');
      }
    }
  };

  const handleEditKeyword = (keyword) => {
    setEditingKeyword(keyword);
    setKeywordFormData({
      keyword: keyword.keyword || '',
      description: keyword.description || '',
      type: keyword.type || 'tag',
      status: keyword.status || 1
    });
    setShowKeywordForm(true);
  };

  // Order Management
  const handleOrderStatusUpdate = async (orderId, newStatus) => {
    try {
      const result = await fetchAPI(`/api/admin/orders/${orderId}/status`, 'PUT', { status: newStatus });
      if (result.success) {
        loadOrders();
        showNotification('Order status updated successfully!', 'success');
      } else {
        showNotification('Failed to update order status: ' + (result.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Update order status error:', error);
      showNotification('Error updating order status: ' + error.message, 'error');
    }
  };

  // Reset Functions
  const resetForm = () => {
    setFormData({
      title: '',
      author: '',
      description: '',
      price: '',
      mrp_price: '',
      sale_price: '',
      category_id: '',
      featured: false,
      rating: '4.00',
      tagline: '',
      thumbnail_url: '',
      stock_quantity: 10,
      allow_download: true,
      allow_online_read: true,
      pdf_file: null,
      pdf_file_url: '',
      sample_pdf: null,
      sample_pdf_url: '',
      product_images: [],
      existing_images: [],
      selected_keywords: [],
      available_keywords: [],
      
      gst_included: false,
      default_discount: 0,
      coupon_code: '',
      coupon_discount: 0,
      seo_keywords: '',
      meta_description: ''
    });
  };

  const resetCategoryForm = () => {
    setCategoryFormData({
      name: '',
      description: '',
      status: 1,
      icon: '📚'
    });
  };

  const resetKeywordForm = () => {
    setKeywordFormData({
      keyword: '',
      description: '',
      type: 'tag',
      status: 1
    });
  };

  const handleFormClose = () => {
    setShowProductForm(false);
    setEditingProduct(null);
    resetForm();
    setUploading(false);
    setPdfUploadProgress({ main: 0, sample: 0 });
    setDraggedImage(null);
    setDragOverImage(null);
    setLoadingImages(false);
  };

  // Formatting Functions
  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Unknown';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const parseOrderItems = (itemsString) => {
    if (!itemsString) return [];
    try {
      return JSON.parse(itemsString);
    } catch (e) {
      return [];
    }
  };

  const handleRetryApi = async () => {
    setDbError(null);
    if (activeTab === 'categories') {
      await loadCategories();
    } else if (activeTab === 'keywords') {
      await loadKeywords();
    } else if (activeTab === 'sellers') {
      await loadSellers();
    } else {
      await loadDashboardData();
    }
  };

  const handleLogout = () => {
    clearAllTokens();
    showNotification('Logged out successfully', 'info');
    window.location.reload();
  };

  const clearAllTokens = () => {
    const tokenKeys = ['token', 'neoteeforge_auth_token', 'auth_token', 'neotee_auth'];
    tokenKeys.forEach(key => localStorage.removeItem(key));
  };

  const showNotification = (message, type = 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ 
      show: false, 
      message: '', 
      type: '' 
    }), 5000);
  };

  const handlePdfFileChange = (e, isSample = false) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        showNotification('Only PDF files are allowed', 'error');
        e.target.value = '';
        return;
      }
      
      if (isSample) {
        setFormData(prev => ({ ...prev, sample_pdf: file }));
      } else {
        setFormData(prev => ({ ...prev, pdf_file: file }));
      }
    }
  };

  const handleRemovePdf = (isSample = false) => {
    if (isSample) {
      setFormData(prev => ({ 
        ...prev, 
        sample_pdf: null,
        sample_pdf_url: '',
      }));
      showNotification('Sample PDF removed', 'info');
    } else {
      setFormData(prev => ({ 
        ...prev, 
        pdf_file: null,
        pdf_file_url: '',
      }));
      showNotification('Main PDF removed', 'info');
    }
  };

  // Load all data
  useEffect(() => {
    if (auth) {
      loadDashboardData();
      loadAllData();
    }
  }, [auth]);

  useEffect(() => {
    if (activeTab === 'products' && auth) {
      loadProducts();
    } else if (activeTab === 'orders' && auth) {
      loadOrders();
    } else if (activeTab === 'users' && auth) {
      loadUsers();
    } else if (activeTab === 'categories' && auth) {
      loadCategories();
    } else if (activeTab === 'keywords' && auth) {
      loadKeywords();
    } else if (activeTab === 'sellers' && auth) {
      loadSellers();
      loadPayouts();
    }
  }, [activeTab, auth]);

  const loadAllData = async () => {
    await loadCategories();
    await loadKeywords();
  };

  // Seller Statistics
  const getSellerStats = (seller) => {
    return {
      totalProducts: seller.product_count || 0,
      totalOrders: seller.order_count || 0,
      totalSales: seller.total_sales || 0,
      conversionRate: seller.conversion_rate || 0
    };
  };

  const getPayoutStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Pending', className: 'pending' },
      processing: { label: 'Processing', className: 'processing' },
      completed: { label: 'Completed', className: 'completed' },
      failed: { label: 'Failed', className: 'failed' },
      cancelled: { label: 'Cancelled', className: 'cancelled' }
    };
    
    const statusInfo = statusMap[status] || { label: 'Unknown', className: 'unknown' };
    
    return (
      <span className={`status-badge ${statusInfo.className}`}>
        {statusInfo.label}
      </span>
    );
  };

  // Image Gallery Preview Component with Drag and Drop
  const renderImageGallery = () => {
    return (
      <div className="image-gallery-section">
        <h4 className="section-title">🖼️ Product Images</h4>
        
        {/* Loading indicator for images */}
        {loadingImages && (
          <div className="image-loading">
            <div className="spinner small"></div>
            <span>Loading images...</span>
          </div>
        )}
        
        {/* Existing Images with Drag and Drop */}
        {!loadingImages && formData.existing_images && formData.existing_images.length > 0 && (
          <div className="existing-images">
            <div className="section-header">
              <h5>Existing Images ({formData.existing_images.length})</h5>
              <div className="section-actions">
                <span className="drag-hint">Drag images to reorder</span>
              </div>
            </div>
            <div className="images-grid">
              {formData.existing_images.map((image, index) => (
                <div 
                  key={image.id || index} 
                  className={`image-preview ${dragOverImage === index ? 'drag-over' : ''} ${draggedImage === index ? 'dragging' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="drag-handle">⋮⋮</div>
                  <img 
                    src={`${API_BASE_URL}/uploads/images/${image.thumbnail_name || image.image_name}`} 
                    alt={`Product ${index + 1}`}
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=200&h=200&fit=crop';
                    }}
                  />
                  <div className="image-badges">
                    {image.is_primary && <span className="primary-badge">Primary</span>}
                    <span className="position-badge">#{index + 1}</span>
                  </div>
                  <div className="image-actions">
                    {!image.is_primary && (
                      <button 
                        type="button"
                        className="btn small primary"
                        onClick={() => handleSetPrimaryImage(image.id)}
                        title="Set as primary image"
                      >
                        Set Primary
                      </button>
                    )}
                    <button 
                      type="button"
                      className="btn small danger"
                      onClick={() => handleRemoveExistingImage(image.id)}
                      title="Delete image"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* No existing images message */}
        {!loadingImages && formData.existing_images && formData.existing_images.length === 0 && (
          <div className="no-images-message">
            <p>No images uploaded yet for this product.</p>
          </div>
        )}
        
        {/* New Images to Upload */}
        <div className="new-images">
          <h5>Upload New Images</h5>
          <div className="image-upload-area">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageFileChange}
              disabled={uploading || imageUploading}
              ref={imageFileInputRef}
              style={{ display: 'none' }}
              id="image-upload-input"
            />
            <label 
              htmlFor="image-upload-input" 
              className="upload-area"
            >
              <div className="upload-icon">📁</div>
              <div className="upload-text">
                <strong>Click to select images</strong>
                <small>or drag and drop here</small>
                <small>Supports JPG, PNG, GIF, WebP (Max 5MB each)</small>
              </div>
            </label>
            
            {imageUploading && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${imageUploadProgress}%` }}
                  ></div>
                </div>
                <span>Uploading images: {imageUploadProgress}%</span>
              </div>
            )}
          </div>
          
          {/* Selected Images Preview */}
          {formData.product_images && formData.product_images.length > 0 && (
            <div className="selected-images">
              <h6>Selected Images ({formData.product_images.length})</h6>
              <div className="images-grid">
                {formData.product_images.map((file, index) => (
                  <div key={index} className="image-preview">
                    <img 
                      src={URL.createObjectURL(file)} 
                      alt={`Selected ${index + 1}`}
                    />
                    <div className="image-actions">
                      <button 
                        type="button"
                        className="btn small danger"
                        onClick={() => handleRemoveImage(index)}
                      >
                        Remove
                      </button>
                    </div>
                    <div className="image-info">
                      {file.name.length > 20 ? file.name.substring(0, 17) + '...' : file.name}
                      <br />
                      <small>{(file.size / 1024 / 1024).toFixed(2)} MB</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Price Preview Component
  const renderPricePreview = () => {
    const priceData = calculatePriceDisplay();
    
    return (
      <div className="price-preview-section">
        <h4 className="section-title">💰 Price Preview</h4>
        <div className="price-preview">
          <div className="price-breakdown">
            <div className="price-row">
              <span>MRP:</span>
              <span className="mrp-price">{formatCurrency(priceData.mrp)}</span>
            </div>
            
            {priceData.salePrice > 0 && (
              <div className="price-row">
                <span>Sale Price:</span>
                <span className="sale-price">{formatCurrency(priceData.salePrice)}</span>
              </div>
            )}
            
            {priceData.effectiveDiscount > 0 && (
              <div className="price-row">
                <span>Discount ({priceData.effectiveDiscount}%):</span>
                <span className="discount-amount">-{formatCurrency(priceData.mrp - priceData.discountedPrice)}</span>
              </div>
            )}
            
            <div className="price-row">
              <span>Discounted Price:</span>
              <span className="discounted-price">{formatCurrency(priceData.discountedPrice)}</span>
            </div>
            
            {!priceData.gstIncluded && (
              <div className="price-row">
                <span>GST (18%):</span>
                <span className="gst-amount">+{formatCurrency(priceData.gstAmount)}</span>
              </div>
            )}
            
            <div className="price-row final-price">
              <span><strong>Final Price:</strong></span>
              <span className="final-price-amount"><strong>{formatCurrency(priceData.finalPrice)}</strong></span>
            </div>
            
            {priceData.savings > 0 && (
              <div className="price-savings">
                <span className="savings-label">You Save:</span>
                <span className="savings-amount">{formatCurrency(priceData.savings)} ({priceData.savingsPercentage}%)</span>
              </div>
            )}
            
            <div className="price-notes">
              <small>GST Status: {priceData.gstIncluded ? 'Included in price' : 'Will be added at checkout'}</small>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Modals
  const renderProductFormModal = () => {
    if (!showProductForm) return null;
    
    const priceData = calculatePriceDisplay();
    
    return (
      <div className="modal-overlay">
        <div className="modal product-form-modal">
          <div className="modal-header">
            <h3>{editingProduct ? 'Edit Product' : 'Add New Product'}</h3>
            <button className="close-btn" onClick={handleFormClose} disabled={uploading}>×</button>
          </div>
          <form onSubmit={editingProduct ? handleUpdateProduct : handleCreateProduct}>
            <div className="form-grid">
              <div className="form-group full-width">
                <h4 className="section-title">📚 Basic Information</h4>
              </div>
              
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  placeholder="Enter product title"
                  disabled={uploading}
                />
              </div>
              
              <div className="form-group">
                <label>Author *</label>
                <input
                  type="text"
                  value={formData.author}
                  onChange={(e) => setFormData({...formData, author: e.target.value})}
                  required
                  placeholder="Enter author name"
                  disabled={uploading}
                />
              </div>
              
              <div className="form-group">
                <label>Tagline</label>
                <input
                  type="text"
                  value={formData.tagline}
                  onChange={(e) => setFormData({...formData, tagline: e.target.value})}
                  placeholder="Enter catchy tagline"
                  disabled={uploading}
                />
              </div>
              
              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows="3"
                  placeholder="Enter product description"
                  disabled={uploading}
                />
              </div>
              
              <div className="form-group full-width">
                <h4 className="section-title">💰 Pricing (Etsy Style)</h4>
              </div>
              
              <div className="form-group">
                <label>MRP (Maximum Retail Price) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.mrp_price}
                  onChange={(e) => setFormData({...formData, mrp_price: e.target.value})}
                  required
                  placeholder="Original price"
                  disabled={uploading}
                />
              </div>
              
              <div className="form-group">
                <label>Sale Price (Optional)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.sale_price}
                  onChange={(e) => setFormData({...formData, sale_price: e.target.value})}
                  placeholder="Discounted price"
                  disabled={uploading}
                />
                <small>Leave empty if no sale price</small>
              </div>
              
              <div className="form-group">
                <label>Default Discount (%)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.default_discount}
                  onChange={(e) => setFormData({...formData, default_discount: e.target.value})}
                  placeholder="0"
                  disabled={uploading}
                />
              </div>
              
              <div className="form-group">
                <label>Coupon Code</label>
                <input
                  type="text"
                  value={formData.coupon_code}
                  onChange={(e) => setFormData({...formData, coupon_code: e.target.value})}
                  placeholder="e.g., SAVE20"
                  disabled={uploading}
                />
              </div>
              
              <div className="form-group">
                <label>Coupon Discount (%)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.coupon_discount}
                  onChange={(e) => setFormData({...formData, coupon_discount: e.target.value})}
                  placeholder="0"
                  disabled={uploading}
                />
                <small>Higher discount between default and coupon will be applied</small>
              </div>
              
              <div className="form-group">
                <label>GST (18%)</label>
                <div className="toggle-group">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={formData.gst_included}
                      onChange={(e) => setFormData({...formData, gst_included: e.target.checked})}
                      disabled={uploading}
                    />
                    <span className="toggle-slider"></span>
                    <span className="toggle-text">
                      {formData.gst_included ? 'GST Included in Price' : 'GST Will Be Added'}
                    </span>
                  </label>
                </div>
              </div>
              
              {/* Price Preview */}
              {renderPricePreview()}
              
              <div className="form-group">
                <label>Category *</label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                  required
                  disabled={uploading}
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.icon} {category.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Stock Quantity</label>
                <input
                  type="number"
                  min="0"
                  value={formData.stock_quantity}
                  onChange={(e) => setFormData({...formData, stock_quantity: e.target.value})}
                  required
                  placeholder="10"
                  disabled={uploading}
                />
              </div>
              
              <div className="form-group">
                <label>Rating</label>
                <select
                  value={formData.rating}
                  onChange={(e) => setFormData({...formData, rating: e.target.value})}
                  disabled={uploading}
                >
                  <option value="4.00">4.0 Stars</option>
                  <option value="4.50">4.5 Stars</option>
                  <option value="5.00">5.0 Stars</option>
                </select>
              </div>
              
              {/* Image Upload Section with Drag and Drop */}
              {renderImageGallery()}
              
              {/* SEO SECTION */}
              <div className="form-group full-width">
                <h4 className="section-title">🔍 SEO Optimization</h4>
              </div>
              
              <div className="form-group full-width">
                <label>SEO Keywords</label>
                <textarea
                  value={formData.seo_keywords}
                  onChange={(e) => setFormData({...formData, seo_keywords: e.target.value})}
                  rows="2"
                  placeholder="Enter SEO keywords separated by commas (e.g., digital book, ebook, self-help, business)"
                  disabled={uploading}
                />
                <small>Keywords for search engines</small>
              </div>
              
              <div className="form-group full-width">
                <label>Meta Description</label>
                <textarea
                  value={formData.meta_description}
                  onChange={(e) => setFormData({...formData, meta_description: e.target.value})}
                  rows="3"
                  placeholder="Enter meta description for search engines (150-160 characters recommended)"
                  disabled={uploading}
                  maxLength="160"
                />
                <small>{formData.meta_description.length}/160 characters</small>
              </div>
              
              <div className="form-group full-width">
                <h4 className="section-title">📄 PDF Files</h4>
                <p className="section-subtitle">Upload PDF files for digital book</p>
              </div>
              
              <div className="form-group full-width">
                <div className="pdf-upload-section">
                  <div className="pdf-upload-header">
                    <label>Main PDF (Full Book) *</label>
                    {formData.pdf_file_url && (
                      <div className="existing-pdf-info">
                        <span className="pdf-icon">📄</span>
                        <span className="pdf-name">Existing: {formData.pdf_file_url.split('/').pop()}</span>
                        <a 
                          href={formData.pdf_file_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn small primary"
                        >
                          View
                        </a>
                        <button 
                          type="button" 
                          className="btn small danger"
                          onClick={() => handleRemovePdf(false)}
                          disabled={uploading}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handlePdfFileChange(e, false)}
                    disabled={uploading}
                    required={!editingProduct}
                    ref={pdfFileInputRef}
                  />
                  <small>Upload the complete book PDF {editingProduct ? '(optional)' : '*'}</small>
                  {uploading && pdfUploadProgress.main > 0 && (
                    <div className="upload-progress">
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{ width: `${pdfUploadProgress.main}%` }}
                        ></div>
                      </div>
                      <span>Uploading main PDF: {pdfUploadProgress.main}%</span>
                    </div>
                  )}
                  {formData.pdf_file && (
                    <div className="selected-file">
                      Selected: {formData.pdf_file.name} ({(formData.pdf_file.size / 1024 / 1024).toFixed(2)} MB)
                    </div>
                  )}
                </div>
              </div>
              
              <div className="form-group full-width">
                <div className="pdf-upload-section">
                  <div className="pdf-upload-header">
                    <label>Sample PDF (Optional)</label>
                    {formData.sample_pdf_url && (
                      <div className="existing-pdf-info">
                        <span className="pdf-icon">📄</span>
                        <span className="pdf-name">Existing: {formData.sample_pdf_url.split('/').pop()}</span>
                        <a 
                          href={formData.sample_pdf_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn small primary"
                        >
                          View
                        </a>
                        <button 
                          type="button" 
                          className="btn small danger"
                          onClick={() => handleRemovePdf(true)}
                          disabled={uploading}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handlePdfFileChange(e, true)}
                    disabled={uploading}
                    ref={samplePdfFileInputRef}
                  />
                  <small>Upload sample/preview version</small>
                  {uploading && pdfUploadProgress.sample > 0 && (
                    <div className="upload-progress">
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{ width: `${pdfUploadProgress.sample}%` }}
                        ></div>
                      </div>
                      <span>Uploading sample PDF: {pdfUploadProgress.sample}%</span>
                    </div>
                  )}
                  {formData.sample_pdf && (
                    <div className="selected-file">
                      Selected: {formData.sample_pdf.name} ({(formData.sample_pdf.size / 1024 / 1024).toFixed(2)} MB)
                    </div>
                  )}
                </div>
              </div>
              
              <div className="form-group full-width">
                <h4 className="section-title">⚡ Features</h4>
              </div>
              
              <div className="form-group full-width">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({...formData, featured: e.target.checked})}
                    disabled={uploading}
                  />
                  Featured Product
                </label>
              </div>
              <div className="form-group full-width">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.allow_download}
                    onChange={(e) => setFormData({...formData, allow_download: e.target.checked})}
                    disabled={uploading}
                  />
                  Allow PDF Download
                </label>
              </div>
              <div className="form-group full-width">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.allow_online_read}
                    onChange={(e) => setFormData({...formData, allow_online_read: e.target.checked})}
                    disabled={uploading}
                  />
                  Allow Online Reading
                </label>
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn primary" disabled={uploading}>
                {uploading ? (
                  <>
                    <span className="spinner small"></span>
                    {editingProduct ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  editingProduct ? 'Update Product' : 'Create Product'
                )}
              </button>
              <button type="button" className="btn secondary" onClick={handleFormClose} disabled={uploading}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const renderCategoryFormModal = () => {
    if (!showCategoryForm) return null;
    
    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="modal-header">
            <h3>{editingCategory ? 'Edit Category' : 'Add New Category'}</h3>
            <button className="close-btn" onClick={() => {
              setShowCategoryForm(false);
              setEditingCategory(null);
              resetCategoryForm();
            }} disabled={uploading}>×</button>
          </div>
          
          <form onSubmit={editingCategory ? handleUpdateCategory : handleCreateCategory}>
            <div className="form-grid">
              <div className="form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData({...categoryFormData, name: e.target.value})}
                  required
                  placeholder="Enter category name"
                  disabled={uploading}
                />
              </div>
              
              <div className="form-group">
                <label>Icon</label>
                <select
                  value={categoryFormData.icon}
                  onChange={(e) => setCategoryFormData({...categoryFormData, icon: e.target.value})}
                  disabled={uploading}
                >
                  <option value="📚">📚 Books</option>
                  <option value="📖">📖 Novel</option>
                  <option value="🧠">🧠 Self Development</option>
                  <option value="💼">💼 Business</option>
                  <option value="📜">📜 History</option>
                  <option value="🔬">🔬 Science</option>
                  <option value="❤️">❤️ Health</option>
                  <option value="💻">💻 Technology</option>
                  <option value="🎨">🎨 Arts</option>
                  <option value="🎵">🎵 Music</option>
                  <option value="🍳">🍳 Cooking</option>
                  <option value="⚽">⚽ Sports</option>
                </select>
              </div>
              
              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  value={categoryFormData.description}
                  onChange={(e) => setCategoryFormData({...categoryFormData, description: e.target.value})}
                  rows="3"
                  placeholder="Enter category description"
                  disabled={uploading}
                />
              </div>
              
              <div className="form-group">
                <label>Status</label>
                <select
                  value={categoryFormData.status}
                  onChange={(e) => setCategoryFormData({...categoryFormData, status: parseInt(e.target.value)})}
                  disabled={uploading}
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </div>
            </div>
            
            <div className="form-actions">
              <button type="submit" className="btn primary" disabled={uploading}>
                {uploading ? (
                  <>
                    <span className="spinner small"></span>
                    {editingCategory ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  editingCategory ? 'Update Category' : 'Create Category'
                )}
              </button>
              <button type="button" className="btn secondary" onClick={() => {
                setShowCategoryForm(false);
                setEditingCategory(null);
                resetCategoryForm();
              }} disabled={uploading}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const renderKeywordFormModal = () => {
    if (!showKeywordForm) return null;
    
    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="modal-header">
            <h3>{editingKeyword ? 'Edit Keyword' : 'Add New Keyword'}</h3>
            <button className="close-btn" onClick={() => {
              setShowKeywordForm(false);
              setEditingKeyword(null);
              resetKeywordForm();
            }} disabled={uploading}>×</button>
          </div>
          
          <form onSubmit={editingKeyword ? handleUpdateKeyword : handleCreateKeyword}>
            <div className="form-grid">
              <div className="form-group">
                <label>Keyword *</label>
                <input
                  type="text"
                  value={keywordFormData.keyword}
                  onChange={(e) => setKeywordFormData({...keywordFormData, keyword: e.target.value})}
                  required
                  placeholder="Enter keyword"
                  disabled={uploading}
                />
              </div>
              
              <div className="form-group">
                <label>Type</label>
                <select
                  value={keywordFormData.type}
                  onChange={(e) => setKeywordFormData({...keywordFormData, type: e.target.value})}
                  disabled={uploading}
                >
                  <option value="tag">Tag</option>
                  <option value="tagline">Tagline</option>
                  <option value="keyword">Keyword</option>
                </select>
              </div>
              
              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  value={keywordFormData.description}
                  onChange={(e) => setKeywordFormData({...keywordFormData, description: e.target.value})}
                  rows="3"
                  placeholder="Enter keyword description"
                  disabled={uploading}
                />
              </div>
              
              <div className="form-group">
                <label>Status</label>
                <select
                  value={keywordFormData.status}
                  onChange={(e) => setKeywordFormData({...keywordFormData, status: parseInt(e.target.value)})}
                  disabled={uploading}
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </div>
            </div>
            
            <div className="form-actions">
              <button type="submit" className="btn primary" disabled={uploading}>
                {uploading ? (
                  <>
                    <span className="spinner small"></span>
                    {editingKeyword ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  editingKeyword ? 'Update Keyword' : 'Create Keyword'
                )}
              </button>
              <button type="button" className="btn secondary" onClick={() => {
                setShowKeywordForm(false);
                setEditingKeyword(null);
                resetKeywordForm();
              }} disabled={uploading}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const renderSellerFormModal = () => {
    if (!showSellerForm) return null;
    
    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="modal-header">
            <h3>{editingSeller ? 'Edit Seller' : 'Add New Seller'}</h3>
            <button className="close-btn" onClick={() => {
              setShowSellerForm(false);
              setEditingSeller(null);
              resetSellerForm();
            }} disabled={uploading}>×</button>
          </div>
          
          <form onSubmit={editingSeller ? handleUpdateSeller : handleCreateSeller}>
            <div className="form-grid">
              <div className="form-group">
                <label>User ID *</label>
                <input
                  type="number"
                  value={sellerFormData.user_id}
                  onChange={(e) => setSellerFormData({...sellerFormData, user_id: e.target.value})}
                  required
                  placeholder="Enter user ID"
                  disabled={uploading || editingSeller}
                />
              </div>
              
              <div className="form-group">
                <label>Store Name *</label>
                <input
                  type="text"
                  value={sellerFormData.store_name}
                  onChange={(e) => setSellerFormData({...sellerFormData, store_name: e.target.value})}
                  required
                  placeholder="Enter store name"
                  disabled={uploading}
                />
              </div>
              
              <div className="form-group">
                <label>Commission Rate (%) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="50"
                  value={sellerFormData.commission_rate}
                  onChange={(e) => setSellerFormData({...sellerFormData, commission_rate: parseFloat(e.target.value)})}
                  required
                  placeholder="15"
                  disabled={uploading}
                />
              </div>
              
              <div className="form-group">
                <label>Status</label>
                <select
                  value={sellerFormData.status}
                  onChange={(e) => setSellerFormData({...sellerFormData, status: e.target.value})}
                  disabled={uploading}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
              
              <div className="form-group full-width">
                <label>Store Description</label>
                <textarea
                  value={sellerFormData.store_description}
                  onChange={(e) => setSellerFormData({...sellerFormData, store_description: e.target.value})}
                  rows="3"
                  placeholder="Enter store description"
                  disabled={uploading}
                />
              </div>
              
              <div className="form-group">
                <label>Contact Email</label>
                <input
                  type="email"
                  value={sellerFormData.contact_email}
                  onChange={(e) => setSellerFormData({...sellerFormData, contact_email: e.target.value})}
                  placeholder="seller@example.com"
                  disabled={uploading}
                />
              </div>
              
              <div className="form-group">
                <label>Contact Phone</label>
                <input
                  type="tel"
                  value={sellerFormData.contact_phone}
                  onChange={(e) => setSellerFormData({...sellerFormData, contact_phone: e.target.value})}
                  placeholder="+91 9876543210"
                  disabled={uploading}
                />
              </div>
              
              <div className="form-group full-width">
                <label>Store Address</label>
                <textarea
                  value={sellerFormData.store_address}
                  onChange={(e) => setSellerFormData({...sellerFormData, store_address: e.target.value})}
                  rows="2"
                  placeholder="Enter store address"
                  disabled={uploading}
                />
              </div>
              
              <div className="form-group">
                <label>Tax ID (GST)</label>
                <input
                  type="text"
                  value={sellerFormData.tax_id}
                  onChange={(e) => setSellerFormData({...sellerFormData, tax_id: e.target.value})}
                  placeholder="GSTIN number"
                  disabled={uploading}
                />
              </div>
              
              <div className="form-group">
                <label>Bank Account Number</label>
                <input
                  type="text"
                  value={sellerFormData.bank_account_number}
                  onChange={(e) => setSellerFormData({...sellerFormData, bank_account_number: e.target.value})}
                  placeholder="Account number"
                  disabled={uploading}
                />
              </div>
              
              <div className="form-group">
                <label>Bank Name</label>
                <input
                  type="text"
                  value={sellerFormData.bank_name}
                  onChange={(e) => setSellerFormData({...sellerFormData, bank_name: e.target.value})}
                  placeholder="Bank name"
                  disabled={uploading}
                />
              </div>
              
              <div className="form-group">
                <label>IFSC Code</label>
                <input
                  type="text"
                  value={sellerFormData.ifsc_code}
                  onChange={(e) => setSellerFormData({...sellerFormData, ifsc_code: e.target.value})}
                  placeholder="IFSC code"
                  disabled={uploading}
                />
              </div>
            </div>
            
            <div className="form-actions">
              <button type="submit" className="btn primary" disabled={uploading}>
                {uploading ? (
                  <>
                    <span className="spinner small"></span>
                    {editingSeller ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  editingSeller ? 'Update Seller' : 'Create Seller'
                )}
              </button>
              <button type="button" className="btn secondary" onClick={() => {
                setShowSellerForm(false);
                setEditingSeller(null);
                resetSellerForm();
              }} disabled={uploading}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const renderPayoutModal = () => {
    if (!showPayoutModal || !selectedPayout) return null;
    
    const seller = sellers.find(s => s.id === selectedPayout.seller_id);
    
    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="modal-header">
            <h3>💰 Process Payout</h3>
            <button className="close-btn" onClick={() => {
              setShowPayoutModal(false);
              setSelectedPayout(null);
              resetPayoutForm();
            }}>×</button>
          </div>
          
          <div className="payout-details">
            <div className="payout-info">
              <h4>Payout Details</h4>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Seller:</span>
                  <span className="info-value">{seller?.store_name || 'Unknown Seller'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Requested Amount:</span>
                  <span className="info-value">{formatCurrency(selectedPayout.amount)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Requested Date:</span>
                  <span className="info-value">{formatDate(selectedPayout.requested_at)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Current Balance:</span>
                  <span className="info-value">{formatCurrency(seller?.balance || 0)}</span>
                </div>
              </div>
            </div>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              handleProcessPayout(selectedPayout.id);
            }}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Amount to Pay *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={selectedPayout.amount}
                    value={payoutFormData.amount}
                    onChange={(e) => setPayoutFormData({...payoutFormData, amount: e.target.value})}
                    required
                    placeholder="Enter amount"
                  />
                </div>
                
                <div className="form-group">
                  <label>Payment Method *</label>
                  <select
                    value={payoutFormData.payment_method}
                    onChange={(e) => setPayoutFormData({...payoutFormData, payment_method: e.target.value})}
                    required
                  >
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="upi">UPI</option>
                    <option value="paypal">PayPal</option>
                    <option value="cash">Cash</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Transaction ID</label>
                  <input
                    type="text"
                    value={payoutFormData.transaction_id}
                    onChange={(e) => setPayoutFormData({...payoutFormData, transaction_id: e.target.value})}
                    placeholder="Enter transaction ID"
                  />
                </div>
                
                <div className="form-group full-width">
                  <label>Notes</label>
                  <textarea
                    value={payoutFormData.notes}
                    onChange={(e) => setPayoutFormData({...payoutFormData, notes: e.target.value})}
                    rows="3"
                    placeholder="Enter payment notes"
                  />
                </div>
              </div>
              
              <div className="form-actions">
                <button type="submit" className="btn primary">
                  ✅ Process Payout
                </button>
                <button type="button" className="btn secondary" onClick={() => {
                  setShowPayoutModal(false);
                  setSelectedPayout(null);
                  resetPayoutForm();
                }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  // If not authenticated
  if (!auth) {
    return (
      <div className="admin-dashboard">
        <div className="admin-header">
          <h1>Admin Dashboard</h1>
          <p>Access denied. Please login as admin.</p>
          <button onClick={onBack} className="btn secondary">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Enhanced Product Display with Pricing
  const renderProductWithPricing = (product) => {
    const thumbnailUrl = getThumbnailUrl(product);
    const mrp = product.mrp_price || product.price || 0;
    const salePrice = product.sale_price || 0;
    const defaultDiscount = product.default_discount || 0;
    const couponDiscount = product.coupon_discount || 0;
    const gstIncluded = product.gst_included === 1;
    
    const basePrice = salePrice > 0 ? salePrice : mrp;
    const effectiveDiscount = Math.max(defaultDiscount, couponDiscount);
    const discountedPrice = basePrice - (basePrice * effectiveDiscount / 100);
    const finalPrice = gstIncluded ? discountedPrice : discountedPrice * 1.18;
    
    return (
      <div key={product.id} className="product-card">
        <div className="product-image">
          <img 
            src={thumbnailUrl}
            alt={product.title}
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=400&h=300&fit=crop&auto=format';
            }} 
          />
          {product.featured && <span className="featured-badge">Featured</span>}
          {product.images && product.images.length > 0 && (
            <span className="image-count-badge">{product.images.length} 📸</span>
          )}
        </div>
        <div className="product-info">
          <h4>{product.title}</h4>
          {product.tagline && (
            <p className="tagline">{product.tagline}</p>
          )}
          <p className="author">by {product.author || 'Unknown Author'}</p>
          <p className="description">{(product.description || '').substring(0, 100)}...</p>
          
          <div className="product-pricing">
            <div className="price-display">
              {salePrice > 0 && (
                <span className="original-price">
                  <del>{formatCurrency(mrp)}</del>
                </span>
              )}
              <span className="current-price">
                {formatCurrency(finalPrice)}
              </span>
              {effectiveDiscount > 0 && (
                <span className="discount-badge">
                  {effectiveDiscount}% OFF
                </span>
              )}
            </div>
            {!gstIncluded && (
              <small className="gst-note">+ 18% GST</small>
            )}
          </div>
          
          <div className="product-meta">
            <span className="category">{getCategoryName(product.category_id)}</span>
            <span className="rating">⭐ {product.rating || '4.0'}</span>
            <span className="stock">Stock: {product.stock_quantity || 0}</span>
          </div>
          <div className="product-features">
            {product.allow_download && <span className="feature">📥 Download</span>}
            {product.allow_online_read && <span className="feature">📖 Online Read</span>}
          </div>
          
          {product.seo_keywords && (
            <div className="product-seo">
              <small>SEO: {product.seo_keywords.substring(0, 50)}...</small>
            </div>
          )}
        </div>
        <div className="product-actions">
          <button 
            className="btn small primary"
            onClick={() => handleEditProduct(product)}
          >
            Edit
          </button>
          <button 
            className="btn small danger"
            onClick={() => handleDeleteProduct(product.id)}
          >
            Delete
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="admin-dashboard">
      {/* Notification */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      {/* All Modals */}
      {renderProductFormModal()}
      {renderCategoryFormModal()}
      {renderKeywordFormModal()}
      {renderSellerFormModal()}
      {renderPayoutModal()}

      <div className="admin-header">
        <div className="header-left">
          <h1>📊 Admin Dashboard</h1>
          <p>Manage your bookstore efficiently with Etsy-like features</p>
        </div>
        <div className="header-actions">
          <button onClick={onBack} className="btn secondary">
            ← Back to Home
          </button>
          <button onClick={handleLogout} className="btn warning">
            🚪 Logout
          </button>
          <button onClick={handleRetryApi} className="btn primary">
            🔄 Refresh
          </button>
          <button onClick={checkDatabaseConnection} className="btn info">
            🔧 Test DB
          </button>
          {dbError && (
            <button onClick={fixDatabaseTables} className="btn danger">
              🛠️ Fix Database
            </button>
          )}
        </div>
      </div>

      {/* Database Error Banner */}
      {dbError && (
        <div className="db-error-banner">
          <div className="db-error-content">
            <span className="db-error-icon">⚠️</span>
            <div className="db-error-details">
              <strong>Database Error:</strong>
              <p>{dbError}</p>
            </div>
            <div className="db-error-actions">
              <button onClick={fixDatabaseTables} className="btn small primary">
                Fix Database
              </button>
              <button onClick={() => setDbError(null)} className="btn small secondary">
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="admin-tabs">
        <button 
          className={`tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
          disabled={loading.dashboard}
        >
          📊 Dashboard {loading.dashboard && '⏳'}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'products' ? 'active' : ''}`}
          onClick={() => setActiveTab('products')}
          disabled={loading.products}
        >
          📚 Products {loading.products ? '⏳' : `(${products.length})`}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'categories' ? 'active' : ''}`}
          onClick={() => setActiveTab('categories')}
          disabled={loading.categories}
        >
          📂 Categories {loading.categories ? '⏳' : `(${categories.length})`}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'keywords' ? 'active' : ''}`}
          onClick={() => setActiveTab('keywords')}
          disabled={loading.keywords}
        >
          🏷️ Keywords {loading.keywords ? '⏳' : `(${keywords.length})`}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'sellers' ? 'active' : ''}`}
          onClick={() => setActiveTab('sellers')}
          disabled={loading.sellers}
        >
          👨‍💼 Sellers {loading.sellers ? '⏳' : `(${sellers.length})`}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
          disabled={loading.orders}
        >
          📦 Orders {loading.orders ? '⏳' : `(${orders.length})`}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
          disabled={loading.users}
        >
          👥 Users {loading.users ? '⏳' : `(${users.length})`}
        </button>
      </div>

      <div className="admin-content">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="dashboard-stats">
            {loading.dashboard ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Loading dashboard data...</p>
              </div>
            ) : (
              <>
                <div className="stat-cards">
                  <div className="stat-card">
                    <div className="stat-icon">📚</div>
                    <div className="stat-info">
                      <h3>Total Products</h3>
                      <div className="stat-number">{stats.overview.totalProducts}</div>
                      <div className="stat-change">{products.length} active</div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">📦</div>
                    <div className="stat-info">
                      <h3>Total Orders</h3>
                      <div className="stat-number">{stats.overview.totalOrders}</div>
                      <div className="stat-change">{stats.today.orders} today</div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">👥</div>
                    <div className="stat-info">
                      <h3>Total Users</h3>
                      <div className="stat-number">{stats.overview.totalUsers}</div>
                      <div className="stat-change">+{stats.today.newUsers} today</div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">💰</div>
                    <div className="stat-info">
                      <h3>Total Revenue</h3>
                      <div className="stat-number">{formatCurrency(stats.overview.totalRevenue)}</div>
                      <div className="stat-change">{formatCurrency(stats.today.revenue)} today</div>
                    </div>
                  </div>
                </div>
                
                <div className="stats-grid">
                  <div className="today-stats">
                    <h3>Today's Overview</h3>
                    <div className="today-items">
                      <div className="today-item">
                        <span className="today-label">New Orders</span>
                        <span className="today-value">{stats.today.orders}</span>
                      </div>
                      <div className="today-item">
                        <span className="today-label">Today's Revenue</span>
                        <span className="today-value">{formatCurrency(stats.today.revenue)}</span>
                      </div>
                      <div className="today-item">
                        <span className="today-label">New Users</span>
                        <span className="today-value">{stats.today.newUsers}</span>
                      </div>
                    </div>
                  </div>

                  <div className="status-stats">
                    <h3>Order Status</h3>
                    <div className="status-items">
                      <div className="status-item pending">
                        <span>Pending Orders</span>
                        <span>{stats.overview.pendingOrders}</span>
                      </div>
                      <div className="status-item completed">
                        <span>Completed Orders</span>
                        <span>{stats.overview.completedOrders}</span>
                      </div>
                      <div className="status-item cancelled">
                        <span>Cancelled Orders</span>
                        <span>{stats.overview.cancelledOrders || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Orders Preview */}
                {stats.recentOrders && stats.recentOrders.length > 0 && (
                  <div className="recent-orders-preview">
                    <div className="section-header">
                      <h3>Recent Orders</h3>
                      <button className="btn secondary small" onClick={() => setActiveTab('orders')}>
                        View All
                      </button>
                    </div>
                    <div className="orders-list">
                      {stats.recentOrders.map(order => (
                        <div key={order.id} className="order-preview-item">
                          <div className="order-info">
                            <strong>Order #{order.id}</strong>
                            <span>{order.customer_name || 'Guest'}</span>
                          </div>
                          <div className="order-details">
                            <span className="amount">{formatCurrency(order.total)}</span>
                            <span className={`status ${order.status}`}>{order.status}</span>
                            <span className="date">{formatDate(order.created_at)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="products-management">
            <div className="section-header">
              <h3>Products Management ({products.length})</h3>
              <div className="section-actions">
                <button className="btn secondary" onClick={loadProducts} disabled={loading.products}>
                  🔄 {loading.products ? 'Loading...' : 'Refresh'}
                </button>
                <button className="btn primary" onClick={() => setShowProductForm(true)}>
                  + Add Product
                </button>
              </div>
            </div>

            {/* Products Grid */}
            {loading.products ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Loading products...</p>
              </div>
            ) : (
              <>
                {products.length > 0 ? (
                  <div className="products-grid">
                    {products.map(product => renderProductWithPricing(product))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No products found. Add your first product!</p>
                    <button 
                      className="btn primary" 
                      onClick={() => setShowProductForm(true)}
                    >
                      + Add Product
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="categories-management">
            <div className="section-header">
              <h3>Categories Management ({categories.length})</h3>
              <div className="section-actions">
                <button className="btn secondary" onClick={loadCategories} disabled={loading.categories}>
                  🔄 {loading.categories ? 'Loading...' : 'Refresh'}
                </button>
                <button className="btn primary" onClick={() => setShowCategoryForm(true)}>
                  + Add Category
                </button>
                {dbError && (
                  <button onClick={fixDatabaseTables} className="btn danger">
                    🛠️ Fix Database
                  </button>
                )}
              </div>
            </div>

            {loading.categories ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Loading categories...</p>
              </div>
            ) : dbError ? (
              <div className="db-error-state">
                <div className="error-icon">⚠️</div>
                <h4>Database Error</h4>
                <p>{dbError}</p>
                <p className="error-suggestion">
                  This error occurs because your database table doesn't have the required columns.
                  Click "Fix Database" to automatically create the missing columns.
                </p>
                <div className="error-actions">
                  <button onClick={fixDatabaseTables} className="btn primary">
                    🛠️ Fix Database Now
                  </button>
                  <button onClick={checkDatabaseConnection} className="btn secondary">
                    🔧 Test Database Connection
                  </button>
                </div>
              </div>
            ) : (
              <>
                {categories.length > 0 ? (
                  <div className="categories-table-container">
                    <table className="categories-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Icon</th>
                          <th>Name</th>
                          <th>Description</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categories.map(category => (
                          <tr key={category.id}>
                            <td className="category-id">#{category.id}</td>
                            <td className="category-icon">{category.icon || '📚'}</td>
                            <td className="category-name">{category.name}</td>
                            <td className="category-desc">{(category.description || '').substring(0, 100)}...</td>
                            <td>
                              <span className={`status-badge ${category.status === 1 ? 'active' : 'inactive'}`}>
                                {category.status === 1 ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="category-actions">
                              <button 
                                className="btn small primary"
                                onClick={() => handleEditCategory(category)}
                              >
                                Edit
                              </button>
                              <button 
                                className="btn small danger"
                                onClick={() => handleDeleteCategory(category.id)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No categories found. Add your first category!</p>
                    <button 
                      className="btn primary" 
                      onClick={() => setShowCategoryForm(true)}
                    >
                      + Add Category
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Keywords Tab */}
        {activeTab === 'keywords' && (
          <div className="keywords-management">
            <div className="section-header">
              <h3>Keywords Management ({keywords.length})</h3>
              <div className="section-actions">
                <button className="btn secondary" onClick={loadKeywords} disabled={loading.keywords}>
                  🔄 {loading.keywords ? 'Loading...' : 'Refresh'}
                </button>
                <button className="btn primary" onClick={() => setShowKeywordForm(true)}>
                  + Add Keyword
                </button>
              </div>
            </div>

            {loading.keywords ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Loading keywords...</p>
              </div>
            ) : (
              <>
                {keywords.length > 0 ? (
                  <div className="keywords-table-container">
                    <table className="keywords-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Keyword</th>
                          <th>Type</th>
                          <th>Description</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {keywords.map(keyword => (
                          <tr key={keyword.id}>
                            <td className="keyword-id">#{keyword.id}</td>
                            <td className="keyword-name">
                              <strong>{keyword.keyword}</strong>
                            </td>
                            <td>
                              <span className={`type-badge ${keyword.type}`}>
                                {keyword.type}
                              </span>
                            </td>
                            <td className="keyword-desc">{(keyword.description || '').substring(0, 100)}...</td>
                            <td>
                              <span className={`status-badge ${keyword.status === 1 ? 'active' : 'inactive'}`}>
                                {keyword.status === 1 ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="keyword-actions">
                              <button 
                                className="btn small primary"
                                onClick={() => handleEditKeyword(keyword)}
                              >
                                Edit
                              </button>
                              <button 
                                className="btn small danger"
                                onClick={() => handleDeleteKeyword(keyword.id)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No keywords found. Add your first keyword!</p>
                    <button 
                      className="btn primary" 
                      onClick={() => setShowKeywordForm(true)}
                    >
                      + Add Keyword
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Sellers Tab */}
        {activeTab === 'sellers' && (
          <div className="sellers-management">
            <div className="section-header">
              <h3>Seller Management ({sellers.length})</h3>
              <div className="section-actions">
                <button className="btn secondary" onClick={loadSellers} disabled={loading.sellers}>
                  🔄 {loading.sellers ? 'Loading...' : 'Refresh'}
                </button>
                <button className="btn primary" onClick={() => setShowSellerForm(true)}>
                  👨‍💼 Add Seller
                </button>
                <button className="btn info" onClick={loadPayouts} disabled={loading.payouts}>
                  💰 Payouts ({payouts.filter(p => p.status === 'pending').length})
                </button>
              </div>
            </div>

            {loading.sellers ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Loading sellers...</p>
              </div>
            ) : (
              <>
                {/* Seller Statistics */}
                <div className="seller-stats-cards">
                  <div className="stat-card">
                    <div className="stat-icon">👨‍💼</div>
                    <div className="stat-info">
                      <h3>Total Sellers</h3>
                      <div className="stat-number">{sellers.length}</div>
                      <div className="stat-change">
                        {sellers.filter(s => s.status === 'approved').length} active
                      </div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">💰</div>
                    <div className="stat-info">
                      <h3>Total Earnings</h3>
                      <div className="stat-number">
                        {formatCurrency(sellers.reduce((sum, seller) => sum + (seller.total_earnings || 0), 0))}
                      </div>
                      <div className="stat-change">
                        {formatCurrency(sellers.reduce((sum, seller) => sum + (seller.balance || 0), 0))} pending
                      </div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">📦</div>
                    <div className="stat-info">
                      <h3>Total Products</h3>
                      <div className="stat-number">
                        {sellers.reduce((sum, seller) => sum + (seller.product_count || 0), 0)}
                      </div>
                      <div className="stat-change">From all sellers</div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon">📊</div>
                    <div className="stat-info">
                      <h3>Avg Commission</h3>
                      <div className="stat-number">
                        {sellers.length > 0 
                          ? (sellers.reduce((sum, seller) => sum + (seller.commission_rate || 0), 0) / sellers.length).toFixed(1) + '%'
                          : '0%'
                        }
                      </div>
                      <div className="stat-change">Average rate</div>
                    </div>
                  </div>
                </div>

                {/* Sellers Table */}
                {sellers.length > 0 ? (
                  <div className="sellers-table-container">
                    <table className="sellers-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Store</th>
                          <th>Owner</th>
                          <th>Products</th>
                          <th>Earnings</th>
                          <th>Commission</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sellers.map(seller => {
                          const stats = getSellerStats(seller);
                          return (
                            <tr key={seller.id}>
                              <td className="seller-id">#{seller.id}</td>
                              <td className="seller-store">
                                <div className="store-info">
                                  <strong>{seller.store_name}</strong>
                                  {seller.store_description && (
                                    <small>{seller.store_description.substring(0, 50)}...</small>
                                  )}
                                </div>
                              </td>
                              <td className="seller-owner">
                                <div>User #{seller.user_id}</div>
                                {seller.contact_email && (
                                  <small>{seller.contact_email}</small>
                                )}
                              </td>
                              <td className="seller-products">
                                <div className="product-count">{stats.totalProducts}</div>
                                <small>{stats.totalOrders} orders</small>
                              </td>
                              <td className="seller-earnings">
                                <div className="earnings-total">{formatCurrency(seller.total_earnings || 0)}</div>
                                <div className="earnings-breakdown">
                                  <small>Balance: {formatCurrency(seller.balance || 0)}</small>
                                  <small>Paid: {formatCurrency(seller.total_payouts || 0)}</small>
                                </div>
                              </td>
                              <td className="seller-commission">
                                <div className="commission-rate">{seller.commission_rate}%</div>
                                <div className="commission-earnings">
                                  <small>Earned: {formatCurrency((seller.total_earnings || 0) * (seller.commission_rate || 0) / 100)}</small>
                                </div>
                              </td>
                              <td className="seller-status">
                                <select 
                                  value={seller.status}
                                  onChange={(e) => handleSellerStatusUpdate(seller.id, e.target.value)}
                                  className={`status-select ${seller.status}`}
                                >
                                  <option value="pending">Pending</option>
                                  <option value="approved">Approved</option>
                                  <option value="rejected">Rejected</option>
                                  <option value="suspended">Suspended</option>
                                </select>
                                <div className="status-date">
                                  <small>{formatDate(seller.approved_at || seller.created_at)}</small>
                                </div>
                              </td>
                              <td className="seller-actions">
                                <div className="action-buttons">
                                  <button 
                                    className="btn small primary"
                                    onClick={() => handleEditSeller(seller)}
                                  >
                                    Edit
                                  </button>
                                  <button 
                                    className="btn small info"
                                    onClick={() => handleSellerCommissionUpdate(seller.id, prompt('Enter new commission rate (%):', seller.commission_rate))}
                                  >
                                    Commission
                                  </button>
                                  <button 
                                    className="btn small danger"
                                    onClick={() => handleDeleteSeller(seller.id)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No sellers found. Add your first seller!</p>
                    <button 
                      className="btn primary" 
                      onClick={() => setShowSellerForm(true)}
                    >
                      👨‍💼 Add Seller
                    </button>
                  </div>
                )}

                {/* Payouts Section */}
                <div className="payouts-section">
                  <div className="section-header">
                    <h4>💰 Payout Requests ({payouts.length})</h4>
                    <div className="section-actions">
                      <span className="status-filter">
                        Pending: {payouts.filter(p => p.status === 'pending').length} |
                        Processing: {payouts.filter(p => p.status === 'processing').length} |
                        Completed: {payouts.filter(p => p.status === 'completed').length}
                      </span>
                    </div>
                  </div>

                  {payouts.length > 0 ? (
                    <div className="payouts-table-container">
                      <table className="payouts-table">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Seller</th>
                            <th>Amount</th>
                            <th>Requested</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payouts.map(payout => {
                            const seller = sellers.find(s => s.id === payout.seller_id);
                            return (
                              <tr key={payout.id}>
                                <td>#{payout.id}</td>
                                <td>{seller?.store_name || 'Unknown Seller'}</td>
                                <td className="payout-amount">
                                  <strong>{formatCurrency(payout.amount)}</strong>
                                </td>
                                <td>{formatDate(payout.requested_at)}</td>
                                <td>{getPayoutStatusBadge(payout.status)}</td>
                                <td>
                                  {payout.status === 'pending' ? (
                                    <button 
                                      className="btn small success"
                                      onClick={() => handleOpenPayoutModal(payout)}
                                    >
                                      Process
                                    </button>
                                  ) : (
                                    <span className="processed-info">
                                      Processed: {formatDate(payout.processed_at)}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>No payout requests found.</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="orders-management">
            <div className="section-header">
              <h3>Orders Management ({orders.length})</h3>
              <button className="btn secondary" onClick={loadOrders} disabled={loading.orders}>
                🔄 {loading.orders ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            {loading.orders ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Loading orders...</p>
              </div>
            ) : (
              <>
                {orders.length > 0 ? (
                  <div className="orders-table-container">
                    <table className="orders-table">
                      <thead>
                        <tr>
                          <th>Order ID</th>
                          <th>Customer</th>
                          <th>Items</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map(order => {
                          const items = parseOrderItems(order.items);
                          const customerInfo = order.shipping_info ? 
                            (typeof order.shipping_info === 'string' ? 
                              JSON.parse(order.shipping_info) : order.shipping_info) : 
                            null;
                          
                          return (
                            <tr key={order.id}>
                              <td>#{order.id}</td>
                              <td>{customerInfo?.fullName || `User #${order.user_id}` || 'Guest'}</td>
                              <td>
                                {items.length > 0 ? `${items.length} items` : '1 item'}
                              </td>
                              <td>{formatCurrency(order.total)}</td>
                              <td>
                                <span className={`status-badge ${order.status}`}>
                                  {order.status}
                                </span>
                              </td>
                              <td>{formatDate(order.created_at)}</td>
                              <td>
                                <select 
                                  value={order.status}
                                  onChange={(e) => handleOrderStatusUpdate(order.id, e.target.value)}
                                  className="status-select"
                                >
                                  <option value="pending">Pending</option>
                                  <option value="confirmed">Confirmed</option>
                                  <option value="processing">Processing</option>
                                  <option value="shipped">Shipped</option>
                                  <option value="delivered">Delivered</option>
                                  <option value="cancelled">Cancelled</option>
                                </select>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No orders found.</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="users-management">
            <div className="section-header">
              <h3>Users Management ({users.length})</h3>
              <button className="btn secondary" onClick={loadUsers} disabled={loading.users}>
                🔄 {loading.users ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            {loading.users ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Loading users...</p>
              </div>
            ) : (
              <>
                {users.length > 0 ? (
                  <div className="users-table-container">
                    <table className="users-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Joined Date</th>
                          <th>Orders</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map(user => (
                          <tr key={user.id}>
                            <td>#{user.id}</td>
                            <td>{user.name}</td>
                            <td>{user.email}</td>
                            <td>
                              <span className={`role-badge ${user.role}`}>
                                {user.role}
                              </span>
                            </td>
                            <td>{formatDate(user.created_at)}</td>
                            <td>{user.order_count || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No users found.</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <style jsx>{`
        .admin-dashboard {
          max-width: 1400px;
          margin: 0 auto;
          padding: 15px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          min-height: 100vh;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        /* Database Error Banner */
        .db-error-banner {
          background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
          color: white;
          border-radius: 12px;
          margin-bottom: 20px;
          padding: 15px 20px;
          animation: slideIn 0.3s ease;
        }

        .db-error-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 15px;
        }

        .db-error-icon {
          font-size: 1.5rem;
          flex-shrink: 0;
        }

        .db-error-details {
          flex: 1;
          min-width: 300px;
        }

        .db-error-details strong {
          display: block;
          margin-bottom: 5px;
          font-size: 1.1rem;
        }

        .db-error-details p {
          margin: 0;
          font-size: 0.9rem;
          opacity: 0.9;
          word-break: break-word;
        }

        .db-error-actions {
          display: flex;
          gap: 10px;
          flex-shrink: 0;
        }

        /* Database Error State */
        .db-error-state {
          text-align: center;
          padding: 40px 20px;
          background: white;
          border-radius: 12px;
          border: 2px solid #dc3545;
        }

        .db-error-state .error-icon {
          font-size: 3rem;
          margin-bottom: 20px;
        }

        .db-error-state h4 {
          color: #dc3545;
          margin-bottom: 15px;
        }

        .db-error-state p {
          color: #495057;
          margin-bottom: 10px;
        }

        .error-suggestion {
          color: #6c757d;
          font-size: 0.9rem;
          max-width: 600px;
          margin: 15px auto;
          line-height: 1.5;
        }

        .error-actions {
          display: flex;
          gap: 15px;
          justify-content: center;
          margin-top: 20px;
          flex-wrap: wrap;
        }

        /* Admin Header */
        .admin-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding: 20px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
          flex-wrap: wrap;
          gap: 15px;
        }

        .header-left h1 {
          font-size: 1.8rem;
          color: #333;
          margin-bottom: 8px;
          font-weight: 700;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .header-left p {
          color: #666;
          font-size: 1rem;
          margin: 0;
        }

        .header-actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        .btn {
          padding: 10px 18px;
          border: none;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          white-space: nowrap;
        }

        .btn.primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .btn.primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
        }

        .btn.secondary {
          background: #6c757d;
          color: white;
        }

        .btn.secondary:hover {
          background: #5a6268;
        }

        .btn.warning {
          background: linear-gradient(135deg, #ffc107 0%, #fd7e14 100%);
          color: white;
        }

        .btn.success {
          background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
          color: white;
        }

        .btn.danger {
          background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
          color: white;
        }

        .btn.info {
          background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
          color: white;
        }

        .btn.small {
          padding: 6px 12px;
          font-size: 0.85rem;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none !important;
        }

        .notification {
          position: fixed;
          top: 15px;
          right: 15px;
          padding: 12px 18px;
          border-radius: 10px;
          color: white;
          font-weight: 600;
          z-index: 10000;
          animation: slideIn 0.3s ease;
          max-width: 350px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          font-size: 0.9rem;
        }

        .notification.success {
          background: linear-gradient(135deg, #28a745, #20c997);
        }

        .notification.error {
          background: linear-gradient(135deg, #dc3545, #c82333);
        }

        .notification.warning {
          background: linear-gradient(135deg, #ffc107, #fd7e14);
        }

        .notification.info {
          background: linear-gradient(135deg, #17a2b8, #138496);
        }

        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        .admin-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 20px;
          padding: 8px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .tab-btn {
          padding: 10px 18px;
          border: none;
          background: none;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;
          color: #666;
          flex-shrink: 0;
        }

        .tab-btn:hover {
          background: #f8f9fa;
        }

        .tab-btn.active {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .tab-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .admin-content {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 5px 20px rgba(0,0,0,0.1);
          min-height: 400px;
        }

        /* Section Header */
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
          flex-wrap: wrap;
          gap: 10px;
        }

        .section-header h3 {
          margin: 0;
          color: #495057;
          font-size: 1.2rem;
          font-weight: 600;
        }

        .section-actions {
          display: flex;
          gap: 8px;
        }

        /* Loading Spinner */
        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid #f3f3f3;
          border-top: 2px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          display: inline-block;
        }

        .spinner.small {
          width: 12px;
          height: 12px;
          border-width: 1px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .loading-spinner {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          text-align: center;
        }

        .loading-spinner .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #f3f3f3;
          border-top: 3px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 15px;
        }

        .loading-spinner p {
          color: #6c757d;
          font-size: 1rem;
          margin: 0;
        }

        .image-loading {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 20px;
          background: #f8f9fa;
          border-radius: 8px;
          margin-bottom: 15px;
        }

        .image-loading span {
          color: #6c757d;
        }

        /* Dashboard Stats */
        .dashboard-stats {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .stat-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
        }

        .stat-card {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 15px;
          transition: all 0.3s ease;
          border: 1px solid #e9ecef;
        }

        .stat-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 5px 15px rgba(0,0,0,0.1);
          border-color: #667eea;
        }

        .stat-icon {
          font-size: 2rem;
          background: white;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 3px 10px rgba(0,0,0,0.08);
          flex-shrink: 0;
        }

        .stat-info h3 {
          margin: 0 0 8px 0;
          color: #495057;
          font-size: 0.9rem;
          font-weight: 600;
        }

        .stat-number {
          font-size: 1.8rem;
          font-weight: 700;
          color: #212529;
          margin-bottom: 4px;
          line-height: 1;
        }

        .stat-change {
          font-size: 0.85rem;
          color: #28a745;
          font-weight: 600;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
        }

        .today-stats, .status-stats {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 20px;
          border: 1px solid #e9ecef;
        }

        .today-stats h3, .status-stats h3 {
          margin-top: 0;
          margin-bottom: 15px;
          color: #495057;
          font-size: 1.1rem;
          font-weight: 600;
        }

        .today-items {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .today-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .today-label {
          color: #6c757d;
          font-weight: 500;
          font-size: 0.9rem;
        }

        .today-value {
          font-weight: 700;
          color: #212529;
          font-size: 1.1rem;
        }

        .status-items {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .status-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .status-item.pending {
          background: #fff3cd;
          color: #856404;
        }

        .status-item.completed {
          background: #d4edda;
          color: #155724;
        }

        .status-item.cancelled {
          background: #f8d7da;
          color: #721c24;
        }

        .recent-orders-preview {
          margin-top: 20px;
        }

        .orders-list {
          background: #f8f9fa;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid #e9ecef;
        }

        .order-preview-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px;
          background: white;
          border-bottom: 1px solid #f8f9fa;
          transition: background 0.3s ease;
          flex-wrap: wrap;
          gap: 10px;
        }

        .order-preview-item:hover {
          background: #f8f9fa;
        }

        .order-preview-item:last-child {
          border-bottom: none;
        }

        .order-info {
          display: flex;
          flex-direction: column;
          gap: 6px;
          flex: 1;
          min-width: 200px;
        }

        .order-info strong {
          color: #212529;
          font-size: 1rem;
        }

        .order-info span {
          color: #6c757d;
          font-size: 0.9rem;
        }

        .order-details {
          display: flex;
          align-items: center;
          gap: 15px;
          flex-wrap: wrap;
        }

        .order-details .amount {
          font-weight: 700;
          color: #212529;
          font-size: 1.1rem;
        }

        .order-details .date {
          color: #6c757d;
          font-size: 0.85rem;
        }

        /* Products Grid */
        .products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 15px;
          margin-top: 15px;
        }

        .product-card {
          background: white;
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 3px 10px rgba(0,0,0,0.08);
          transition: transform 0.3s ease;
          display: flex;
          flex-direction: column;
          border: 1px solid #e9ecef;
        }

        .product-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 5px 15px rgba(0,0,0,0.12);
        }

        .product-image {
          position: relative;
          height: 180px;
          overflow: hidden;
        }

        .product-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .product-image:hover img {
          transform: scale(1.05);
        }

        .featured-badge {
          position: absolute;
          top: 8px;
          left: 8px;
          background: #ffc107;
          color: #212529;
          padding: 3px 8px;
          border-radius: 16px;
          font-size: 0.7rem;
          font-weight: 700;
        }

        .image-count-badge {
          position: absolute;
          bottom: 8px;
          right: 8px;
          background: rgba(0,0,0,0.7);
          color: white;
          padding: 3px 8px;
          border-radius: 16px;
          font-size: 0.7rem;
          font-weight: 600;
        }

        .product-info {
          padding: 15px;
          flex: 1;
        }

        .product-info h4 {
          margin: 0 0 6px 0;
          color: #212529;
          font-size: 1.1rem;
          font-weight: 600;
          line-height: 1.3;
        }

        .tagline {
          color: #667eea;
          font-style: italic;
          margin-bottom: 8px;
          font-weight: 500;
        }

        .author {
          color: #6c757d;
          font-size: 0.85rem;
          margin-bottom: 8px;
        }

        .description {
          color: #495057;
          font-size: 0.9rem;
          line-height: 1.4;
          margin-bottom: 12px;
        }

        /* Product Pricing Styles */
        .product-pricing {
          margin-bottom: 12px;
          padding: 10px;
          background: #f8f9fa;
          border-radius: 8px;
          border: 1px solid #e9ecef;
        }

        .price-display {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 5px;
          flex-wrap: wrap;
        }

        .original-price {
          color: #6c757d;
          text-decoration: line-through;
          font-size: 0.9rem;
        }

        .current-price {
          color: #28a745;
          font-weight: 700;
          font-size: 1.2rem;
        }

        .discount-badge {
          background: #dc3545;
          color: white;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 0.7rem;
          font-weight: 700;
        }

        .gst-note {
          color: #6c757d;
          font-size: 0.8rem;
          display: block;
        }

        .product-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-bottom: 12px;
        }

        .category, .rating, .stock {
          background: #e9ecef;
          color: #495057;
          padding: 3px 8px;
          border-radius: 16px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .product-features {
          display: flex;
          gap: 6px;
          margin-top: 8px;
          flex-wrap: wrap;
        }

        .product-features .feature {
          background: #d4edda;
          color: #155724;
          padding: 3px 8px;
          border-radius: 16px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .product-seo {
          margin-top: 8px;
          padding: 5px;
          background: #e7f1ff;
          border-radius: 4px;
          border-left: 3px solid #4dabf7;
        }

        .product-seo small {
          color: #1864ab;
          font-size: 0.75rem;
        }

        .product-actions {
          padding: 12px 15px;
          background: #f8f9fa;
          border-top: 1px solid #e9ecef;
          display: flex;
          gap: 8px;
        }

        .product-actions button {
          flex: 1;
          padding: 8px;
          font-size: 0.85rem;
        }

        .empty-state {
          text-align: center;
          padding: 30px 15px;
          color: #6c757d;
        }

        .empty-state p {
          margin-bottom: 15px;
          font-size: 1rem;
        }

        .no-images-message {
          text-align: center;
          padding: 30px;
          background: #f8f9fa;
          border-radius: 8px;
          margin-bottom: 20px;
          color: #6c757d;
        }

        /* Image Gallery Styles with Drag and Drop */
        .image-gallery-section {
          grid-column: 1 / -1;
          margin-bottom: 20px;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
          border: 1px solid #e9ecef;
        }

        .image-gallery-section h4, .image-gallery-section h5, .image-gallery-section h6 {
          margin-top: 0;
          margin-bottom: 15px;
          color: #495057;
        }

        .image-gallery-section h5 {
          font-size: 1rem;
          margin-bottom: 10px;
        }

        .image-gallery-section h6 {
          font-size: 0.9rem;
          margin-bottom: 10px;
        }

        .image-count {
          background: #667eea;
          color: white;
          padding: 4px 8px;
          border-radius: 16px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .drag-hint {
          color: #6c757d;
          font-size: 0.75rem;
        }

        .images-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
          gap: 12px;
          margin-bottom: 15px;
        }

        .image-preview {
          position: relative;
          border-radius: 8px;
          overflow: hidden;
          border: 2px solid #dee2e6;
          background: white;
          transition: all 0.2s ease;
          cursor: grab;
          user-select: none;
        }

        .image-preview.dragging {
          opacity: 0.5;
          transform: scale(0.95);
          cursor: grabbing;
        }

        .image-preview.drag-over {
          border-color: #28a745;
          transform: scale(1.02);
        }

        .drag-handle {
          position: absolute;
          top: 5px;
          left: 5px;
          background: rgba(0,0,0,0.5);
          color: white;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 0.8rem;
          cursor: grab;
          z-index: 2;
        }

        .image-preview img {
          width: 100%;
          height: 120px;
          object-fit: cover;
          display: block;
          pointer-events: none;
        }

        .image-badges {
          position: absolute;
          top: 5px;
          right: 5px;
          display: flex;
          gap: 5px;
          z-index: 2;
        }

        .primary-badge {
          background: #28a745;
          color: white;
          padding: 3px 8px;
          border-radius: 12px;
          font-size: 0.7rem;
          font-weight: 600;
          text-align: center;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }

        .position-badge {
          background: #6c757d;
          color: white;
          padding: 3px 8px;
          border-radius: 12px;
          font-size: 0.7rem;
          font-weight: 600;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }

        .image-actions {
          display: flex;
          flex-direction: column;
          gap: 5px;
          padding: 8px;
          background: rgba(0, 0, 0, 0.7);
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          opacity: 0;
          transition: opacity 0.3s ease;
          z-index: 2;
        }

        .image-preview:hover .image-actions {
          opacity: 1;
        }

        .image-actions .btn.small {
          padding: 3px 6px;
          font-size: 0.7rem;
          border-radius: 4px;
        }

        .image-info {
          padding: 5px;
          background: white;
          font-size: 0.7rem;
          color: #6c757d;
          text-align: center;
          border-top: 1px solid #dee2e6;
          word-break: break-all;
          max-height: 40px;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .image-info small {
          color: #6c757d;
        }

        .upload-area {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 20px;
          border: 2px dashed #dee2e6;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          transition: all 0.3s ease;
          text-align: center;
          margin-bottom: 10px;
        }

        .upload-area:hover {
          border-color: #667eea;
          background: #f8f9fa;
        }

        .upload-icon {
          font-size: 2rem;
          margin-bottom: 10px;
          color: #6c757d;
        }

        .upload-text {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .upload-text strong {
          color: #495057;
          font-size: 0.9rem;
        }

        .upload-text small {
          color: #6c757d;
          font-size: 0.8rem;
        }

        /* Price Preview Styles */
        .price-preview-section {
          grid-column: 1 / -1;
          margin-bottom: 20px;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
          border: 1px solid #e9ecef;
        }

        .price-preview {
          background: white;
          border-radius: 8px;
          padding: 15px;
          border: 1px solid #dee2e6;
        }

        .price-breakdown {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .price-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #f8f9fa;
        }

        .price-row:last-child {
          border-bottom: none;
        }

        .price-row.final-price {
          border-top: 2px solid #dee2e6;
          padding-top: 12px;
          margin-top: 8px;
        }

        .mrp-price {
          color: #6c757d;
          text-decoration: line-through;
        }

        .sale-price {
          color: #28a745;
          font-weight: 600;
        }

        .discount-amount {
          color: #dc3545;
          font-weight: 600;
        }

        .discounted-price {
          color: #495057;
          font-weight: 600;
        }

        .gst-amount {
          color: #17a2b8;
          font-weight: 600;
        }

        .final-price-amount {
          color: #28a745;
          font-size: 1.2rem;
        }

        .price-savings {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px;
          background: #d4edda;
          border-radius: 6px;
          margin-top: 10px;
        }

        .savings-label {
          color: #155724;
          font-weight: 600;
        }

        .savings-amount {
          color: #155724;
          font-weight: 700;
          font-size: 1.1rem;
        }

        .price-notes {
          margin-top: 10px;
          padding: 8px;
          background: #fff3cd;
          border-radius: 4px;
          border-left: 3px solid #ffc107;
        }

        .price-notes small {
          color: #856404;
          font-size: 0.8rem;
        }

        /* Toggle Switch */
        .toggle-group {
          margin-top: 5px;
        }

        .toggle-label {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          user-select: none;
        }

        .toggle-label input[type="checkbox"] {
          display: none;
        }

        .toggle-slider {
          position: relative;
          width: 50px;
          height: 26px;
          background: #ccc;
          border-radius: 34px;
          transition: .4s;
        }

        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 4px;
          bottom: 4px;
          background: white;
          border-radius: 50%;
          transition: .4s;
        }

        .toggle-label input:checked + .toggle-slider {
          background: #28a745;
        }

        .toggle-label input:checked + .toggle-slider:before {
          transform: translateX(24px);
        }

        .toggle-text {
          color: #495057;
          font-size: 0.9rem;
          font-weight: 500;
        }

        /* Categories & Keywords Tables */
        .categories-table-container, .keywords-table-container {
          overflow-x: auto;
          margin-top: 15px;
          border-radius: 8px;
          border: 1px solid #e9ecef;
          -webkit-overflow-scrolling: touch;
        }

        .categories-table, .keywords-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.9rem;
          min-width: 600px;
        }

        .categories-table th, .keywords-table th {
          padding: 12px;
          text-align: left;
          color: #495057;
          font-weight: 600;
          border-bottom: 1px solid #dee2e6;
          white-space: nowrap;
        }

        .categories-table td, .keywords-table td {
          padding: 12px;
          border-bottom: 1px solid #dee2e6;
          vertical-align: middle;
        }

        .categories-table tbody tr:hover, .keywords-table tbody tr:hover {
          background: #f8f9fa;
        }

        .category-icon {
          font-size: 1.2rem;
          text-align: center;
        }

        .category-name {
          font-weight: 600;
        }

        .category-desc, .keyword-desc {
          color: #6c757d;
          max-width: 200px;
        }

        .keyword-name {
          font-weight: 600;
        }

        .category-actions, .keyword-actions {
          white-space: nowrap;
        }

        /* Orders Table */
        .orders-table-container {
          overflow-x: auto;
          margin-top: 15px;
          border-radius: 8px;
          border: 1px solid #e9ecef;
          -webkit-overflow-scrolling: touch;
        }

        .orders-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.9rem;
          min-width: 800px;
        }

        .orders-table thead {
          background: #f8f9fa;
        }

        .orders-table th {
          padding: 12px;
          text-align: left;
          color: #495057;
          font-weight: 600;
          border-bottom: 1px solid #dee2e6;
          white-space: nowrap;
        }

        .orders-table td {
          padding: 12px;
          border-bottom: 1px solid #dee2e6;
          vertical-align: middle;
        }

        .orders-table tbody tr:hover {
          background: #f8f9fa;
        }

        .status-badge {
          padding: 4px 10px;
          border-radius: 16px;
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .status-badge.active {
          background: #d4edda;
          color: #155724;
        }

        .status-badge.inactive {
          background: #f8d7da;
          color: #721c24;
        }

        .status-badge.pending {
          background: #fff3cd;
          color: #856404;
        }

        .status-badge.confirmed, .status-badge.processing {
          background: #cff4fc;
          color: #055160;
        }

        .status-badge.shipped {
          background: #d1e7dd;
          color: #0f5132;
        }

        .status-badge.delivered {
          background: #d4edda;
          color: #155724;
        }

        .status-badge.cancelled {
          background: #f8d7da;
          color: #721c24;
        }

        .status-select {
          padding: 6px 10px;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          font-size: 0.85rem;
          background: white;
          cursor: pointer;
          transition: border-color 0.3s ease;
          width: 100%;
          max-width: 120px;
        }

        .status-select:focus {
          outline: none;
          border-color: #667eea;
        }

        /* Users Table */
        .users-table-container {
          overflow-x: auto;
          margin-top: 15px;
          border-radius: 8px;
          border: 1px solid #e9ecef;
          -webkit-overflow-scrolling: touch;
        }

        .users-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.9rem;
          min-width: 500px;
        }

        .users-table thead {
          background: #f8f9fa;
        }

        .users-table th {
          padding: 12px;
          text-align: left;
          color: #495057;
          font-weight: 600;
          border-bottom: 1px solid #dee2e6;
          white-space: nowrap;
        }

        .users-table td {
          padding: 12px;
          border-bottom: 1px solid #dee2e6;
          vertical-align: middle;
        }

        .users-table tbody tr:hover {
          background: #f8f9fa;
        }

        .role-badge {
          padding: 4px 10px;
          border-radius: 16px;
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .role-badge.admin {
          background: #667eea;
          color: white;
        }

        .role-badge.customer {
          background: #28a745;
          color: white;
        }

        /* Seller Management Styles */
        .sellers-management {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .seller-stats-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 15px;
          margin-bottom: 20px;
        }

        .sellers-table-container, .payouts-table-container {
          overflow-x: auto;
          margin-top: 15px;
          border-radius: 8px;
          border: 1px solid #e9ecef;
          -webkit-overflow-scrolling: touch;
        }

        .sellers-table, .payouts-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.9rem;
          min-width: 800px;
        }

        .sellers-table thead, .payouts-table thead {
          background: #f8f9fa;
        }

        .sellers-table th, .payouts-table th {
          padding: 12px;
          text-align: left;
          color: #495057;
          font-weight: 600;
          border-bottom: 1px solid #dee2e6;
          white-space: nowrap;
        }

        .sellers-table td, .payouts-table td {
          padding: 12px;
          border-bottom: 1px solid #dee2e6;
          vertical-align: middle;
        }

        .sellers-table tbody tr:hover, .payouts-table tbody tr:hover {
          background: #f8f9fa;
        }

        .seller-id {
          font-weight: 600;
          color: #495057;
        }

        .seller-store .store-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .seller-store small {
          color: #6c757d;
          font-size: 0.8rem;
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .seller-owner {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .seller-owner small {
          color: #6c757d;
          font-size: 0.8rem;
        }

        .seller-products {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
        }

        .product-count {
          background: #007bff;
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.9rem;
        }

        .seller-products small {
          color: #6c757d;
          font-size: 0.8rem;
        }

        .seller-earnings {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .earnings-total {
          font-weight: 700;
          color: #28a745;
          font-size: 1rem;
        }

        .earnings-breakdown {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .earnings-breakdown small {
          color: #6c757d;
          font-size: 0.75rem;
        }

        .seller-commission {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .commission-rate {
          background: #6f42c1;
          color: white;
          padding: 4px 8px;
          border-radius: 16px;
          font-weight: 700;
          font-size: 0.9rem;
          text-align: center;
          width: 50px;
        }

        .commission-earnings small {
          color: #6c757d;
          font-size: 0.75rem;
        }

        .seller-status {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .status-select.pending {
          border-color: #ffc107;
          color: #856404;
          background: #fff3cd;
        }

        .status-select.approved {
          border-color: #28a745;
          color: #155724;
          background: #d4edda;
        }

        .status-select.rejected {
          border-color: #dc3545;
          color: #721c24;
          background: #f8d7da;
        }

        .status-select.suspended {
          border-color: #6c757d;
          color: #343a40;
          background: #e9ecef;
        }

        .status-select:focus {
          outline: none;
          border-color: #667eea;
        }

        .status-date small {
          color: #6c757d;
          font-size: 0.75rem;
        }

        .seller-actions .action-buttons {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }

        .seller-actions .btn.small {
          padding: 4px 8px;
          font-size: 0.8rem;
          min-width: 70px;
        }

        /* Payouts Section */
        .payouts-section {
          margin-top: 30px;
          padding: 20px;
          background: #f8f9fa;
          border-radius: 12px;
          border: 1px solid #e9ecef;
        }

        .payouts-section .section-header {
          margin-bottom: 15px;
        }

        .status-filter {
          color: #6c757d;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .payout-amount strong {
          color: #28a745;
          font-size: 1rem;
        }

        .processed-info {
          color: #6c757d;
          font-size: 0.8rem;
        }

        /* Payout Modal Styles */
        .payout-details {
          padding: 20px;
        }

        .payout-info {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .payout-info h4 {
          margin-top: 0;
          margin-bottom: 15px;
          color: #495057;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .info-label {
          color: #6c757d;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .info-value {
          color: #212529;
          font-weight: 600;
        }

        /* Status Badges for Payouts */
        .status-badge.pending {
          background: #fff3cd;
          color: #856404;
        }

        .status-badge.processing {
          background: #cff4fc;
          color: #055160;
        }

        .status-badge.completed {
          background: #d4edda;
          color: #155724;
        }

        .status-badge.failed {
          background: #f8d7da;
          color: #721c24;
        }

        .status-badge.cancelled {
          background: #e9ecef;
          color: #343a40;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 10px;
        }

        .modal {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 900px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 15px 30px rgba(0,0,0,0.2);
        }

        .product-form-modal {
          max-width: 1000px;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px 20px;
          border-bottom: 1px solid #f8f9fa;
          position: sticky;
          top: 0;
          background: white;
          z-index: 1;
        }

        .modal-header h3 {
          margin: 0;
          color: #212529;
          font-size: 1.3rem;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #6c757d;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.3s ease;
          flex-shrink: 0;
        }

        .close-btn:hover {
          background: #f8f9fa;
        }

        /* Form Styles */
        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 15px;
          margin-bottom: 20px;
          padding: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group.full-width {
          grid-column: 1 / -1;
        }

        .form-group label {
          margin-bottom: 6px;
          color: #495057;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          padding: 10px;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          font-size: 0.95rem;
          transition: border-color 0.3s ease;
          width: 100%;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #667eea;
        }

        .form-group textarea {
          resize: vertical;
          min-height: 80px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-weight: normal;
          user-select: none;
          font-size: 0.9rem;
        }

        .checkbox-label input[type="checkbox"] {
          width: 16px;
          height: 16px;
          cursor: pointer;
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          padding: 15px 20px;
          border-top: 1px solid #f8f9fa;
        }

        /* PDF Upload Section */
        .pdf-upload-section {
          padding: 12px;
          background: #f8f9fa;
          border-radius: 8px;
          border: 2px dashed #dee2e6;
        }

        .pdf-upload-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
          flex-wrap: wrap;
          gap: 8px;
        }

        .existing-pdf-info {
          display: flex;
          align-items: center;
          gap: 8px;
          background: white;
          padding: 6px 10px;
          border-radius: 6px;
          border: 1px solid #dee2e6;
          flex-wrap: wrap;
        }

        .pdf-icon {
          font-size: 1rem;
        }

        .pdf-name {
          font-family: 'Courier New', monospace;
          font-size: 0.85rem;
          color: #495057;
          max-width: 150px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .upload-progress {
          margin-top: 8px;
          padding: 8px;
          background: white;
          border-radius: 6px;
          border: 1px solid #dee2e6;
          font-size: 0.85rem;
        }

        .progress-bar {
          width: 100%;
          height: 6px;
          background: #e9ecef;
          border-radius: 4px;
          overflow: hidden;
          margin-bottom: 6px;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #28a745, #20c997);
          transition: width 0.3s ease;
        }

        .selected-file {
          margin-top: 8px;
          padding: 6px 10px;
          background: #d4edda;
          color: #155724;
          border-radius: 6px;
          font-size: 0.85rem;
          border: 1px solid #c3e6cb;
          word-break: break-all;
        }

        /* Section Title */
        .section-title {
          color: #495057;
          font-size: 1.1rem;
          margin: 0 0 8px 0;
          padding-bottom: 8px;
          border-bottom: 2px solid #e9ecef;
        }

        .section-subtitle {
          color: #6c757d;
          font-size: 0.9rem;
          margin-bottom: 12px;
        }

        /* Type Badges */
        .type-badge {
          padding: 3px 8px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .type-badge.tag {
          background: #28a745;
          color: white;
        }

        .type-badge.tagline {
          background: #17a2b8;
          color: white;
        }

        .type-badge.keyword {
          background: #ffc107;
          color: #212529;
        }

        /* Responsive styles */
        @media (max-width: 768px) {
          .admin-dashboard {
            padding: 10px;
          }

          .admin-header {
            padding: 15px;
            flex-direction: column;
            align-items: stretch;
            text-align: center;
          }

          .header-left h1 {
            font-size: 1.6rem;
            text-align: center;
          }

          .header-actions {
            justify-content: center;
          }

          .btn {
            padding: 8px 15px;
            font-size: 0.9rem;
          }

          .admin-tabs {
            flex-wrap: nowrap;
            overflow-x: auto;
          }

          .tab-btn {
            padding: 8px 15px;
            font-size: 0.85rem;
          }

          .admin-content {
            padding: 15px;
          }

          .stat-cards {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }

          .stat-card {
            padding: 15px;
            flex-direction: column;
            text-align: center;
            gap: 10px;
          }

          .stat-icon {
            width: 50px;
            height: 50px;
            font-size: 1.5rem;
          }

          .stat-number {
            font-size: 1.5rem;
          }

          .stat-info h3 {
            font-size: 0.85rem;
          }

          .products-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .stats-grid {
            grid-template-columns: 1fr;
            gap: 15px;
          }

          .section-header {
            flex-direction: column;
            align-items: stretch;
            text-align: center;
          }

          .section-actions {
            justify-content: center;
          }

          .order-preview-item {
            flex-direction: column;
            align-items: stretch;
            text-align: left;
          }

          .order-details {
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
          }

          .modal {
            margin: 5px;
          }

          .form-grid {
            grid-template-columns: 1fr;
            gap: 12px;
            padding: 15px;
          }

          .pdf-name {
            max-width: 120px;
          }

          .existing-pdf-info {
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
          }

          .existing-pdf-info .btn {
            width: 100%;
            justify-content: center;
          }

          .seller-stats-cards {
            grid-template-columns: repeat(2, 1fr);
          }

          .sellers-table, .payouts-table {
            font-size: 0.8rem;
          }

          .seller-actions .action-buttons {
            flex-direction: column;
          }

          .seller-actions .btn.small {
            min-width: 60px;
            padding: 3px 6px;
          }

          .status-select {
            width: 100px;
          }

          .info-grid {
            grid-template-columns: 1fr;
          }

          .db-error-content {
            flex-direction: column;
            text-align: center;
          }

          .db-error-details {
            min-width: auto;
          }

          .error-actions {
            flex-direction: column;
          }

          .images-grid {
            grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
          }

          .image-preview img {
            height: 100px;
          }

          .image-actions .btn.small {
            padding: 2px 4px;
            font-size: 0.6rem;
          }
        }

        @media (max-width: 480px) {
          .admin-header h1 {
            font-size: 1.4rem;
          }

          .header-actions {
            flex-direction: column;
          }

          .btn {
            width: 100%;
            justify-content: center;
          }

          .tab-btn {
            padding: 8px 12px;
            font-size: 0.8rem;
          }

          .stat-cards {
            grid-template-columns: 1fr;
          }

          .today-item, .status-item {
            padding: 10px;
            font-size: 0.85rem;
          }

          .product-card {
            margin-bottom: 10px;
          }

          .product-actions {
            flex-direction: column;
          }

          .selected-file {
            font-size: 0.8rem;
            word-break: break-word;
          }

          .seller-stats-cards {
            grid-template-columns: 1fr;
          }

          .seller-actions .action-buttons {
            flex-direction: column;
            align-items: stretch;
          }

          .seller-actions .btn.small {
            width: 100%;
            margin-bottom: 5px;
          }

          .images-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .image-preview img {
            height: 80px;
          }
        }

        @media (max-width: 360px) {
          .admin-tabs {
            flex-direction: column;
          }

          .tab-btn {
            width: 100%;
            justify-content: center;
          }

          .stat-card {
            padding: 12px;
          }

          .today-stats, .status-stats {
            padding: 15px;
          }

          .form-actions {
            flex-direction: column;
          }

          .form-actions .btn {
            width: 100%;
          }

          .images-grid {
            grid-template-columns: 1fr;
          }
        }

        /* Additional classes for IDs */
        .category-id, .keyword-id {
          font-weight: 600;
          color: #495057;
        }
      `}</style>
    </div>
  );
};

export default AdminDashboard;